/* ============================================================
   VITALCASA CRM — ModalNuevoLead (tema claro/oscuro)
   Modal para creación/edición de leads con panel historial.

   Props:
   - isOpen: boolean
   - onClose: () => void
   - onCreated: () => void  (callback para recargar leads)
   - lead: objeto lead (si se pasa → modo edición)
   ============================================================ */

import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import {
  LEAD_IDIOMA_OPTIONS,
  resolveEffectiveLeadIdioma,
  formatIdiomaBadge,
  normalizeLanguageCode,
} from '../utils/leadLanguage';
import { useNavigate } from 'react-router-dom';
import { useTheme } from './ThemeProvider';
import api from '../services/api';
import { emojiVisualProcedencia, fuenteVisibleEnLead, mismaProcedencia } from '../utils/procedenciaColor';
import TaskCurrentCard from './TaskCurrentCard';
import FichaResumenPropiedad from './propiedades/FichaResumenPropiedad';
import CitaLinkReferencia from './cita/CitaLinkReferencia';
import { resolverPropiedadIdPorReferencia } from '../utils/resolverPropiedadIdPorReferencia';
import ModalWhatsApp from './ModalWhatsApp';
import ModalEmail from './ModalEmail';
import ModalResultadoCualificacion from './ModalResultadoCualificacion';
import ModalResultadoVisita from './ModalResultadoVisita';
import ModalResultadoTareaAgente, { isAgenteResultTask } from './ModalResultadoTareaAgente';
import ModalCita from './ModalCita';
import { buildCitaDateTimeISO, addMinutosHora, ensureVisitaRangoISO, fetchPropiedadPorRef } from '../utils/citaAgenda';
import { PuntoWaHistorial } from './historial/PuntoWaHistorial';
import { IconoWhatsAppHistorial, tileWhatsAppHistorial, WA_VERDE_OFICIAL } from './historial/IconoWhatsAppHistorial';
import { tileLlamadaHistorial } from './historial/IconoLlamadaHistorial';
import { textoCompletoHistorialWa } from '../utils/waPlantillasTexto';

function parseHistorialMetadata(item) {
  const raw = item?.metadata;
  if (!raw) return {};
  if (typeof raw === 'object') return raw;
  try { return JSON.parse(raw); } catch { return {}; }
}

function WaHistorialAudio({ item, style = {} }) {
  const meta = parseHistorialMetadata(item);
  const url = meta.wa_audio_url;
  if (!url) return null;
  return (
    <div style={{ marginTop: 6, ...style }}>
      <div style={{ fontSize: 10, color: '#64748b', marginBottom: 4, fontWeight: 600 }}>Audio original</div>
      <audio controls preload="none" src={url} style={{ width: '100%', maxWidth: 320, height: 32 }} />
    </div>
  );
}
import {
  HISTORIAL_FILTRO_IDS,
  etiquetaWhatsAppEnviadoHistorial,
  etiquetaHistorialConAutor,
  historialPasaFiltros,
  categoriaTipoHistorial,
} from '../utils/historialFiltros';
import { ModalMoverLeadTerminal } from './LeadArchivoActions';
import {
  taskNeedsM1Modal,
  taskNeedsM2Modal,
  taskIsM1Type,
  taskIsM2Type,
  isLeadTaskExpired,
  isWhatsappWaitingTask,
  hasWhatsappReplyPending,
  isWhatsappFlowTask,
  parseTaskMetadata,
  isT4bObjecionesDescarteTask,
  isT4b1AutoContactTask,
  isT4b1Task,
  isVisitFeedbackTask,
  getVisitAgendaId,
} from '../constants/leadFlowOutcomes';
import { dialogs } from '../context/DialogContext';
import { useAuth } from '../context/AuthContext';
import usePermiso from '../hooks/usePermiso';
import {
  LEAD_FLOW_TASK_OPTIONS_FALLBACK,
  taskRequiereCitaAntesDeAlta,
} from '../constants/leadFlowTaskCatalog';

/** Admin / secretaría / director: pueden forzar alta con teléfono/email exacto ya existente. */
const ROLES_CREAR_CON_CLIENTE_DUPLICADO = ['admin', 'administrador', 'director', 'secretaria'];
function puedeCrearLeadConClienteDuplicadoExacto(usuario) {
  return ROLES_CREAR_CON_CLIENTE_DUPLICADO.includes(String(usuario?.rol || '').toLowerCase());
}

const MSG_DEBE_ASIGNAR_TAREA = 'Debe primero asignar tarea';
const MSG_TELEFONO_O_EMAIL = 'Indica al menos un teléfono o un email';
const MSG_CITA_CANCELADA_ELIGE_TAREA =
  'Cita no confirmada. Elige otra tarea o vuelve a confirmar la cita para guardar el lead.';

/** Visual de tarea de embudo: emoji + color por canal (WA / llamada / cita…). */
function estiloTareaFlujo(code, label = '') {
  const c = String(code || '').trim().toUpperCase();
  const l = String(label || '').toLowerCase();
  const blob = `${c} ${l}`;
  if (/venta|\bt7\b/.test(blob)) {
    return { emoji: '🏆', accent: '#ca8a04', bg: 'rgba(202,138,4,0.14)', border: 'rgba(202,138,4,0.45)' };
  }
  if (/cita|\btc\b|agendar|visita/.test(blob) && !/whatsapp|wa\b/.test(blob)) {
    return { emoji: '📅', accent: '#7c3aed', bg: 'rgba(124,58,237,0.12)', border: 'rgba(124,58,237,0.4)' };
  }
  if (/cualif|t2a|\bt4\b/.test(blob) && !/whatsapp|llamada|cita/.test(blob)) {
    return { emoji: '✅', accent: '#059669', bg: 'rgba(5,150,105,0.12)', border: 'rgba(5,150,105,0.4)' };
  }
  if (/buscar.?prop|t3bn|t4bn/.test(blob)) {
    return { emoji: '🔎', accent: '#0284c7', bg: 'rgba(2,132,199,0.12)', border: 'rgba(2,132,199,0.4)' };
  }
  if (/whatsapp|\bwa\b|t1c|t2e|t3b|t4b2|enviar prop/.test(blob)) {
    return { emoji: '💬', accent: '#16a34a', bg: 'rgba(37,211,102,0.14)', border: 'rgba(22,163,74,0.45)' };
  }
  if (/llam|t1r|t1b|t2l|volver a llamar|t4b1/.test(blob)) {
    return { emoji: '📞', accent: '#2563eb', bg: 'rgba(37,99,235,0.12)', border: 'rgba(37,99,235,0.4)' };
  }
  return { emoji: '📋', accent: '#64748b', bg: 'rgba(100,116,139,0.12)', border: 'rgba(100,116,139,0.35)' };
}

/** Selector de tarea inicial del embudo (alta manual / sin tarea vigente). */
function AsignarTareaFlujoBlock({
  options = LEAD_FLOW_TASK_OPTIONS_FALLBACK,
  value = '',
  onSelect,
  c,
  isDark,
  disabled = false,
  highlightError = false,
}) {
  const [abierto, setAbierto] = useState(false);
  const seleccion = (options || []).find((o) => o.code === value) || null;
  const estiloSel = seleccion ? estiloTareaFlujo(seleccion.code, seleccion.label) : null;

  return (
    <div style={{ position: 'relative' }}>
      {!seleccion ? (
        <button
          type="button"
          disabled={disabled}
          onClick={() => setAbierto((v) => !v)}
          style={{
            width: '100%',
            padding: '10px 12px',
            borderRadius: 8,
            border: `2px dashed ${highlightError ? '#ef4444' : (isDark ? '#475569' : '#94a3b8')}`,
            background: isDark ? 'rgba(37,99,235,0.08)' : '#eff6ff',
            color: highlightError ? '#ef4444' : (isDark ? '#93c5fd' : '#1d4ed8'),
            fontSize: 12,
            fontWeight: 700,
            cursor: disabled ? 'not-allowed' : 'pointer',
            fontFamily: 'inherit',
            opacity: disabled ? 0.6 : 1,
          }}
        >
          Asignar tarea
        </button>
      ) : (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            padding: '8px 10px',
            borderRadius: 8,
            border: `2px solid ${estiloSel.border}`,
            background: estiloSel.bg,
          }}
        >
          <span style={{ fontSize: 18, lineHeight: 1, flexShrink: 0 }} aria-hidden>{estiloSel.emoji}</span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: c.textPrimary }}>{seleccion.label}</div>
            <div style={{ fontSize: 10, color: estiloSel.accent, marginTop: 2, fontWeight: 600 }}>Flujo desde esta tarea</div>
          </div>
          {!disabled && (
            <button
              type="button"
              onClick={() => setAbierto((v) => !v)}
              style={{
                padding: '5px 8px',
                borderRadius: 6,
                border: `1px solid ${estiloSel.border}`,
                background: isDark ? 'rgba(0,0,0,0.2)' : 'rgba(255,255,255,0.7)',
                color: estiloSel.accent,
                fontSize: 10,
                fontWeight: 600,
                cursor: 'pointer',
                fontFamily: 'inherit',
                flexShrink: 0,
              }}
            >
              Cambiar
            </button>
          )}
        </div>
      )}
      {abierto && !disabled && (
        <div
          style={{
            position: 'absolute',
            left: 0,
            right: 0,
            top: 'calc(100% + 4px)',
            zIndex: 40,
            maxHeight: 280,
            overflowY: 'auto',
            borderRadius: 10,
            border: `1px solid ${c.border}`,
            background: c.bgCard || c.bgInput,
            boxShadow: '0 10px 28px rgba(0,0,0,0.22)',
            padding: 4,
          }}
        >
          {(options || []).map((opt) => {
            const st = estiloTareaFlujo(opt.code, opt.label);
            const activo = value === opt.code;
            return (
            <button
              key={opt.code}
              type="button"
              onClick={() => {
                onSelect?.(opt.code);
                setAbierto(false);
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                width: '100%',
                textAlign: 'left',
                padding: '9px 10px',
                border: activo ? `1.5px solid ${st.border}` : '1.5px solid transparent',
                borderRadius: 8,
                marginBottom: 2,
                background: activo ? st.bg : 'transparent',
                color: c.textPrimary,
                fontSize: 12,
                fontWeight: activo ? 700 : 500,
                cursor: 'pointer',
                fontFamily: 'inherit',
              }}
            >
              <span style={{
                width: 28, height: 28, borderRadius: 8, flexShrink: 0,
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                background: st.bg, border: `1px solid ${st.border}`, fontSize: 15,
              }} aria-hidden>{st.emoji}</span>
              <span style={{ flex: 1, minWidth: 0, lineHeight: 1.3 }}>{opt.label}</span>
            </button>
            );
          })}
        </div>
      )}
      {highlightError && !seleccion && (
        <div style={{ marginTop: 6, fontSize: 11, fontWeight: 600, color: '#ef4444' }}>
          {MSG_DEBE_ASIGNAR_TAREA}
        </div>
      )}
    </div>
  );
}

const MAX_FUENTES_VISIBLES = 6;
const MAX_TIPOS_VISIBLES   = 7;
const POBLACIONES_CHIP = ['Dénia', 'La Xara', 'El Verger', 'Els Poblets', 'Ondara', 'Pedreguer', 'Pego'];

function mismaPoblacionChip(a, b) {
  const norm = (s) => String(s || '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/['’]/g, '');
  const na = norm(a);
  const nb = norm(b);
  return Boolean(na && nb && na === nb);
}

function parseDatosExtras(lead) {
  if (!lead?.datos_extras) return {};
  if (typeof lead.datos_extras === 'object') return lead.datos_extras;
  try { return JSON.parse(lead.datos_extras); } catch { return {}; }
}

function esTitularAnaLead(lead) {
  return /^ana\b/i.test(String(lead?.agente_nombre || '').trim());
}

function esAgenteAnaSeleccionado(agentesList, agenteId) {
  const agent = (agentesList || []).find((item) => String(item.id) === String(agenteId || ''));
  return /^ana\b/i.test(String(agent?.nombre || '').trim());
}

function agenteIdEnFormulario(lead) {
  if (!lead?.id) return '';
  return lead.agente_id != null ? String(lead.agente_id) : '';
}

/** Comercial normativo (preagente) sin tocar el agente operativo (Ana). */
function preagenteAsignadoInfo(lead, agentesList = []) {
  const extras = parseDatosExtras(lead);
  const id =
    lead?.preagente_asignado_id ||
    extras.preagente_asignado_id ||
    extras.agente_comercial_sugerido_id ||
    extras.asignacion_agente?.agente_id ||
    null;
  const motivo =
    lead?.preagente_motivo ||
    extras.preagente_motivo ||
    extras.asignacion_agente?.motivo ||
    '';
  let nombre = lead?.preagente_nombre || '';
  if (!nombre && id) {
    const found = (agentesList || []).find((a) => String(a.id) === String(id));
    nombre = found?.nombre || '';
  }
  return {
    id: id ? String(id) : null,
    nombre: String(nombre || '').trim(),
    motivo: String(motivo || '').trim(),
  };
}

const ml = {
  blue:       '#2563eb',
  blueFrom:   '#1d4ed8',
  blueTo:     '#2563eb',
  blueLight:  '#3b82f6',
  blueShadow: 'rgba(37,99,235,0.35)',
  blueBg:     'rgba(37,99,235,0.12)',
  blueBorder: 'rgba(37,99,235,0.45)',
  chipText:   '#ffffff',
};

// Todas las provincias de España + extranjero
const PROVINCIAS_ES = [
  { id: '01', nombre: 'Álava' },
  { id: '02', nombre: 'Albacete' },
  { id: '03', nombre: 'Alicante' },
  { id: '04', nombre: 'Almería' },
  { id: '33', nombre: 'Asturias' },
  { id: '05', nombre: 'Ávila' },
  { id: '06', nombre: 'Badajoz' },
  { id: '07', nombre: 'Baleares' },
  { id: '08', nombre: 'Barcelona' },
  { id: '09', nombre: 'Burgos' },
  { id: '10', nombre: 'Cáceres' },
  { id: '11', nombre: 'Cádiz' },
  { id: '39', nombre: 'Cantabria' },
  { id: '12', nombre: 'Castellón' },
  { id: '51', nombre: 'Ceuta' },
  { id: '13', nombre: 'Ciudad Real' },
  { id: '14', nombre: 'Córdoba' },
  { id: '15', nombre: 'A Coruña' },
  { id: '16', nombre: 'Cuenca' },
  { id: '17', nombre: 'Girona' },
  { id: '18', nombre: 'Granada' },
  { id: '19', nombre: 'Guadalajara' },
  { id: '20', nombre: 'Guipúzcoa' },
  { id: '21', nombre: 'Huelva' },
  { id: '22', nombre: 'Huesca' },
  { id: '23', nombre: 'Jaén' },
  { id: '26', nombre: 'La Rioja' },
  { id: '35', nombre: 'Las Palmas' },
  { id: '24', nombre: 'León' },
  { id: '25', nombre: 'Lleida' },
  { id: '27', nombre: 'Lugo' },
  { id: '28', nombre: 'Madrid' },
  { id: '29', nombre: 'Málaga' },
  { id: '52', nombre: 'Melilla' },
  { id: '30', nombre: 'Murcia' },
  { id: '31', nombre: 'Navarra' },
  { id: '32', nombre: 'Ourense' },
  { id: '34', nombre: 'Palencia' },
  { id: '36', nombre: 'Pontevedra' },
  { id: '37', nombre: 'Salamanca' },
  { id: '38', nombre: 'Santa Cruz de Tenerife' },
  { id: '40', nombre: 'Segovia' },
  { id: '41', nombre: 'Sevilla' },
  { id: '42', nombre: 'Soria' },
  { id: '43', nombre: 'Tarragona' },
  { id: '44', nombre: 'Teruel' },
  { id: '45', nombre: 'Toledo' },
  { id: '46', nombre: 'Valencia' },
  { id: '47', nombre: 'Valladolid' },
  { id: '48', nombre: 'Vizcaya' },
  { id: '49', nombre: 'Zamora' },
  { id: '50', nombre: 'Zaragoza' },
  { id: 'EX', nombre: 'Extranjero' },
];

// Provincias con localidades en BD (resto usa input libre)
const PROVINCIAS_CON_BD = ['03', '46', '12'];

const NOTAS_RAPIDAS = [
  { id: 'busca_comprar',      emoji: '🏠', label: 'Busca comprar',    text: 'Cliente interesado en comprar. Busca activamente.' },
  { id: 'busca_alquilar',     emoji: '🔑', label: 'Busca alquilar',   text: 'Cliente interesado en alquilar.' },
  { id: 'urgente',            emoji: '🚨', label: 'Urgente',          text: 'Cliente con urgencia. Priorizar contacto.' },
  { id: 'inversor',           emoji: '💰', label: 'Inversor',         text: 'Perfil inversor. Busca rentabilidad.' },
  { id: 'solo_info',          emoji: 'ℹ️', label: 'Solo información', text: 'De momento solo busca información. No tiene prisa.' },
  { id: 'segunda_residencia', emoji: '🏖️', label: '2ª residencia',   text: 'Busca segunda residencia o casa vacacional.' },
];

const OPERACIONES = [
  { id: 'Venta',    emoji: '🏷️', label: 'Venta' },
  { id: 'Alquiler', emoji: '🔑', label: 'Alquiler' },
];

// ── Subcomponentes definidos FUERA del padre ────────────────
function ChipBtn({ activo, onClick, children, colorActivo, compact = false }) {
  const { theme } = useTheme();
  const c = theme.colors;
  return (
    <button onClick={onClick} style={{
      padding: compact ? '4px 9px' : '5px 12px',
      borderRadius: 6,
      fontSize: compact ? 10 : 11,
      fontWeight: 600,
      border: `1.5px solid ${activo ? (colorActivo || ml.blue) : c.border}`,
      background: activo ? (colorActivo || ml.blue) : c.bgInput,
      color: activo ? '#fff' : c.textSecondary,
      cursor: 'pointer',
      fontFamily: "'DM Sans', 'Segoe UI Emoji', 'Apple Color Emoji', 'Noto Color Emoji', sans-serif",
      transition: 'all 0.15s',
      boxShadow: activo ? `0 2px 8px ${colorActivo ? colorActivo + '55' : ml.blueShadow}` : 'none',
      whiteSpace: compact ? 'nowrap' : 'normal',
    }}>
      {children}
    </button>
  );
}

function MasBtn({ activo, onClick, children }) {
  const { theme } = useTheme();
  const c = theme.colors;
  return (
    <button onClick={onClick} style={{
      padding: '5px 10px', borderRadius: 6, fontSize: 11, fontWeight: 600,
      border: `1.5px solid ${activo ? ml.blueBorder : c.border}`,
      background: activo ? ml.blueBg : c.bgInput,
      color: activo ? ml.chipText : c.textMuted,
      cursor: 'pointer',
      fontFamily: "'DM Sans', 'Segoe UI Emoji', 'Apple Color Emoji', 'Noto Color Emoji', sans-serif",
    }}>
      {children}
    </button>
  );
}

function IconoWhatsApp() {
  return <IconoWhatsAppHistorial size={18} />;
}

const CRITERIO_DBL_MS = 380;

function HistorialFiltroChip({ label, active, excluded, onClick, isDark, c }) {
  const ex = excluded === true;
  const exBg = isDark ? '#991b1b' : '#dc2626';
  const border = ex ? 'transparent' : active ? ml.blue : c.border;
  const background = ex ? exBg : active ? ml.blue : c.bgInput;
  const color = ex || active ? '#ffffff' : c.textMuted;
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        padding: '2px 8px',
        fontSize: 10,
        fontWeight: 500,
        borderRadius: 6,
        border: `1px solid ${border}`,
        background,
        color,
        cursor: 'pointer',
        transition: 'all 0.15s ease',
        userSelect: 'none',
        lineHeight: '1.4',
        fontFamily: 'inherit',
      }}
    >
      {label}
    </button>
  );
}

function inicialesContacto(nombre) {
  const trozos = String(nombre || '').trim().split(/\s+/).filter(Boolean);
  return (trozos.map((x) => x[0]).join('').slice(0, 2) || 'CL').toUpperCase();
}

/** Email del lead o, si falta, del cliente vinculado (p. ej. lead desde Fríos). */
function emailContactoLead(lead, extra = '') {
  if (!lead) return '';
  return String(lead.email || lead.cliente?.email || extra || '').trim();
}

// ── Panel derecho: acciones + historial ────────────────────
function PanelHistorial({ lead, isDark, c, soloLectura, onLeadRefresh, panelOpen = true, taskOptions = LEAD_FLOW_TASK_OPTIONS_FALLBACK, onPedirModalCita }) {
  const navigate = useNavigate();
  const { puede } = usePermiso();
  const verVerCliente = puede('pl_leads_ficha_ver_cliente', 'ver');
  const verAbrirTarea = puede('pl_leads_ficha_abrir_tarea', 'ver');
  const verAccionWhatsapp = puede('pl_leads_ficha_whatsapp', 'ver');
  const verAccionEmail = puede('pl_leads_ficha_email', 'ver');
  const verAccionLlamada = puede('pl_leads_ficha_llamada', 'ver');
  const verAccionNota = puede('pl_leads_ficha_nota', 'ver');
  const verHistorial = puede('pl_leads_ficha_historial', 'ver');
  const [historial,   setHistorial]   = useState([]);
  const [tareaVigente, setTareaVigente] = useState(null);
  const [cargando,    setCargando]    = useState(false);
  const [panelCargado, setPanelCargado] = useState(false);
  const [asignandoTarea, setAsignandoTarea] = useState(false);
  const [errorAsignarTarea, setErrorAsignarTarea] = useState('');
  const [notaTexto,   setNotaTexto]   = useState('');
  const [mostrarNota, setMostrarNota] = useState(false);
  const [historialFiltrosIncl, setHistorialFiltrosIncl] = useState([]);
  const [historialFiltrosExcl, setHistorialFiltrosExcl] = useState([]);
  const historialChipDblRef = useRef({ id: null, t: 0, timer: null });
  const [visorHistorial, setVisorHistorial] = useState(null);
  // Un único modal de tarea activo a la vez: 'whatsapp' | 'cualificacion' | 'visita' | 'agente' | null.
  // Abrir uno cierra automáticamente el anterior (evita modales apilados al avanzar el flujo).
  // IDs de tareas cuyo modal el agente ha cerrado MANUALMENTE en esta sesión del
  // panel. Es un ref (no estado) local al componente: se reinicia al re-montar el
  // panel (al volver a entrar en el lead), de modo que si la tarea sigue pendiente
  // el modal vuelve a salir. Dentro de la misma sesión, una tarea cerrada no se
  // vuelve a auto-abrir aunque el polling refresque la tarea.
  const dismissedTaskIdsRef = useRef(new Set());
  /** T4b1 abierta a mano con «Abrir tarea» (M2) aunque aún no haya respuesta WA. */
  const manualM2T4b1TaskIdRef = useRef(null);
  const [activeTaskModal, setActiveTaskModal] = useState(null);
  const [tareaModalDescartado, setTareaModalDescartado] = useState(false);
  const [creandoCliente, setCreandoCliente] = useState(false);
  const [clienteEmailExtra, setClienteEmailExtra] = useState('');

  const modalWhatsAppOpen = activeTaskModal === 'whatsapp';
  const modalEmailOpen = activeTaskModal === 'email';
  const modalCualificacionOpen = activeTaskModal === 'cualificacion';
  const modalResultadoVisitaOpen = activeTaskModal === 'visita';
  const modalResultadoAgenteOpen = activeTaskModal === 'agente';

  const cerrarSubmodales = useCallback(() => {
    setActiveTaskModal(null);
    setVisorHistorial(null);
    setMostrarNota(false);
  }, []);

  useEffect(() => {
    if (!panelOpen) cerrarSubmodales();
  }, [panelOpen, cerrarSubmodales]);

  useEffect(() => () => cerrarSubmodales(), [cerrarSubmodales]);

  const nombreContacto = lead
    ? [lead.nombre, lead.apellido].filter(Boolean).join(' ').trim() || 'Cliente'
    : '';

  const contactoWhatsApp = lead
    ? {
        nombre: nombreContacto,
        telefono: String(lead.telefono || lead.whatsapp || '').trim(),
        iniciales: inicialesContacto(nombreContacto),
      }
    : null;

  const emailResuelto = useMemo(
    () => emailContactoLead(lead, clienteEmailExtra),
    [lead, clienteEmailExtra],
  );

  const contactoEmail = lead
    ? {
        nombre: nombreContacto,
        email: emailResuelto,
        iniciales: inicialesContacto(nombreContacto),
      }
    : null;

  useEffect(() => {
    setClienteEmailExtra('');
    if (!lead?.cliente_id || emailContactoLead(lead)) return undefined;
    let cancelled = false;
    api.get(`/clientes/${lead.cliente_id}`)
      .then((r) => {
        if (cancelled) return;
        const em = String(r.data?.data?.email || '').trim();
        if (em) setClienteEmailExtra(em);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [lead?.id, lead?.cliente_id, lead?.email, lead?.cliente?.email]);

  const idiomaWhatsApp = useMemo(
    () => resolveEffectiveLeadIdioma({
      idioma: lead?.idioma,
      mensaje: lead?.mensaje_original,
      mensaje_original: lead?.mensaje_original,
      notas: lead?.notas,
    }).code,
    [lead?.idioma, lead?.mensaje_original, lead?.notas],
  );

  const recargarPanelTareaHistorial = async () => {
    if (!lead?.id) return;
    try {
      const [histRes, tareaRes, leadRes] = await Promise.all([
        api.get(`/leads/${lead.id}/historial`),
        api.get(`/tareas/lead/${lead.id}/activa`),
        api.get(`/leads/${lead.id}`),
      ]);
      setHistorial(histRes.data?.data || []);
      setTareaVigente(tareaRes.data?.tarea || null);
      const refreshed = leadRes.data?.data;
      if (refreshed?.id) onLeadRefresh?.(refreshed);
    } catch {
      /* refresco en segundo plano */
    }
  };

  useEffect(() => {
    if (!lead?.id) {
      setHistorial([]);
      setTareaVigente(null);
      setPanelCargado(false);
      return;
    }
    setCargando(true);
    setPanelCargado(false);
    recargarPanelTareaHistorial().finally(() => {
      setCargando(false);
      setPanelCargado(true);
    });
  }, [lead?.id]);

  const recargarPanelPostLlamada = async () => {
    await recargarPanelTareaHistorial();
  };

  const formatFecha = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    return d.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit' }) +
      ' ' + d.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
  };

  const categoriaTipo = (item) => categoriaTipoHistorial(item);

  const scheduleHistorialChipInclExcl = (id, onSingle, onDouble) => {
    const now = Date.now();
    const r = historialChipDblRef.current;
    if (r.id === id && now - r.t < CRITERIO_DBL_MS) {
      if (r.timer) {
        clearTimeout(r.timer);
        r.timer = null;
      }
      r.id = null;
      r.t = 0;
      onDouble();
      return;
    }
    r.id = id;
    r.t = now;
    if (r.timer) clearTimeout(r.timer);
    r.timer = setTimeout(() => {
      r.timer = null;
      r.id = null;
      onSingle();
    }, CRITERIO_DBL_MS);
  };

  const onHistorialFiltroChipPress = (filtroId, isReset = false) => {
    scheduleHistorialChipInclExcl(
      `hist:${filtroId}`,
      () => {
        if (isReset) {
          setHistorialFiltrosIncl([]);
          setHistorialFiltrosExcl([]);
          return;
        }
        setHistorialFiltrosIncl((inc) => {
          if (inc.includes(filtroId)) return inc.filter((x) => x !== filtroId);
          return [...inc, filtroId];
        });
        setHistorialFiltrosExcl((ex) => ex.filter((x) => x !== filtroId));
      },
      () => {
        if (isReset) {
          setHistorialFiltrosIncl([]);
          setHistorialFiltrosExcl([...HISTORIAL_FILTRO_IDS]);
          return;
        }
        setHistorialFiltrosExcl((ex) => {
          if (ex.includes(filtroId)) return ex.filter((x) => x !== filtroId);
          return [...ex, filtroId];
        });
        setHistorialFiltrosIncl((inc) => inc.filter((x) => x !== filtroId));
      },
    );
  };

  const historialSinFiltroActivo =
    historialFiltrosIncl.length === 0 && historialFiltrosExcl.length === 0;

  const iconoTipo = (item) => {
    const cat = categoriaTipo(item);
    if (cat === 'whatsapp') return <IconoWhatsApp />;
    if (cat === 'email')    return <span style={{ fontSize: 15 }}>✉️</span>;
    if (cat === 'llamada')  return <span style={{ fontSize: 15 }}>📞</span>;
    if (cat === 'nota')     return <span style={{ fontSize: 15 }}>📝</span>;
    const t = (item.tipo || '').toLowerCase();
    if (t.includes('lead_creado')) return <span style={{ fontSize: 15 }}>🆕</span>;
    if (t.includes('lead_asignado')) return <span style={{ fontSize: 15 }}>👤</span>;
    if (t.includes('lead_reasignado')) return <span style={{ fontSize: 15 }}>🔁</span>;
    if (t.includes('visita'))      return <span style={{ fontSize: 15 }}>🏠</span>;
    if (t.includes('tarea'))       return <span style={{ fontSize: 15 }}>✅</span>;
    if (t.includes('etapa'))       return <span style={{ fontSize: 15 }}>🔄</span>;
    return <span style={{ fontSize: 15 }}>📌</span>;
  };

  const textoItem = (item) => item.contenido || item.descripcion || item.asunto || item.tipo || '—';
  const autorItem = (item) => {
    if (item._fuente === 'ana') return item.tipo === 'whatsapp_ana' ? '🤖 Ana' : '👤 Lead';
    if (item.tipo === 'whatsapp_recibido') return 'Cliente';
    return item.actor || item.actor_nombre || 'Sistema';
  };

  const metaTipo = (item) => {
    const t = (item.tipo || '').toLowerCase();
    const conAutor = (titulo, { wa = false } = {}) =>
      wa ? titulo : etiquetaHistorialConAutor(titulo, item);
    if (t === 'llamada_ana' || categoriaTipo(item) === 'llamada') {
      const ll = tileLlamadaHistorial();
      return {
        label: conAutor(t === 'llamada_ana' ? 'Llamada Ana (IA)' : 'Llamada'),
        ...ll,
      };
    }
    if (t === 'whatsapp_recibido' || categoriaTipo(item) === 'whatsapp') {
      const wa = tileWhatsAppHistorial(13);
      return {
        label: t === 'whatsapp_recibido'
          ? conAutor('Respuesta WhatsApp')
          : etiquetaWhatsAppEnviadoHistorial(item),
        ...wa,
      };
    }
    if (categoriaTipo(item) === 'email') {
      return {
        label: conAutor(t === 'email_recibido' ? 'Email recibido' : 'Email enviado'),
        accent: '#b45309',
        bg: '#b45309',
        shadow: '0 2px 6px rgba(180,83,9,0.25)',
        icon: <svg width={13} height={13} viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round"><rect x="2" y="4" width="20" height="16" rx="2" /><path d="M22 7l-10 6L2 7" /></svg>,
      };
    }
    if (categoriaTipo(item) === 'nota') {
      return {
        label: conAutor('Nota'),
        accent: '#6b7280',
        bg: '#6b7280',
        shadow: '0 2px 6px rgba(107,114,128,0.22)',
        icon: <svg width={13} height={13} viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" /></svg>,
      };
    }
    if (t === 'lead_creado') {
      return {
        label: conAutor(item.asunto || 'Lead creado'),
        accent: '#2563eb',
        bg: 'linear-gradient(135deg, #2563eb, #1d4ed8)',
        shadow: '0 2px 6px rgba(37,99,235,0.3)',
        icon: <span style={{ fontSize: 13 }}>🆕</span>,
      };
    }
    if (t === 'lead_asignado' || t === 'lead_comercial_sugerido') {
      return {
        label: conAutor(item.asunto || (t === 'lead_comercial_sugerido' ? 'Comercial titular sugerido' : 'Asignación comercial')),
        accent: '#0ea5e9',
        bg: 'linear-gradient(135deg, #0ea5e9, #0284c7)',
        shadow: '0 2px 6px rgba(14,165,233,0.3)',
        icon: <span style={{ fontSize: 13 }}>👤</span>,
      };
    }
    if (t === 'lead_reasignado') {
      return {
        label: conAutor(item.asunto || 'Reasignación de agente'),
        accent: '#f59e0b',
        bg: 'linear-gradient(135deg, #f59e0b, #d97706)',
        shadow: '0 2px 6px rgba(245,158,11,0.3)',
        icon: <span style={{ fontSize: 13 }}>🔁</span>,
      };
    }
    if (t === 'tarea_vencida') {
      return {
        label: conAutor(item.asunto || 'Tarea vencida'),
        accent: '#ef4444',
        bg: 'linear-gradient(135deg, #ef4444, #dc2626)',
        shadow: '0 2px 6px rgba(239,68,68,0.3)',
        icon: <span style={{ fontSize: 13 }}>⏱</span>,
      };
    }
    if (t === 'tarea_flujo') {
      return {
        label: conAutor(item.asunto || 'Flujo de tareas'),
        accent: '#8b5cf6',
        bg: 'linear-gradient(135deg, #8b5cf6, #7c3aed)',
        shadow: '0 2px 6px rgba(139,92,246,0.3)',
        icon: <span style={{ fontSize: 13 }}>✅</span>,
      };
    }
    if (t === 'lead_etapa_auto' || t === 'lead_etapa_manual' || t === 'cambio_etapa' || t === 'etapa_cambio') {
      return {
        label: conAutor(item.asunto || 'Cambio de etapa'),
        accent: '#475569',
        bg: 'linear-gradient(135deg, #475569, #64748b)',
        shadow: '0 2px 6px rgba(71,85,105,0.25)',
        icon: <span style={{ fontSize: 13 }}>🔄</span>,
      };
    }
    return {
      label: conAutor(item.asunto || item.tipo || 'Sistema'),
      accent: '#475569',
      bg: 'linear-gradient(135deg, #475569, #64748b)',
      shadow: '0 2px 6px rgba(71,85,105,0.25)',
      icon: <span style={{ color: '#fff', fontSize: 12, fontWeight: 700 }}>•</span>,
    };
  };

  const renderConversacionCompleta = (item) => {
    const esWa =
      item?.tipo === 'whatsapp_enviado' || item?.tipo === 'whatsapp_recibido';
    const txt = esWa
      ? textoCompletoHistorialWa(item, parseHistorialMetadata(item))
      : (item?.contenido || '');
    const esLlamadaAna = item?.tipo === 'llamada_ana';
    if (!esLlamadaAna) {
      return (
        <>
          <div style={{ fontSize: 13, color: c.textSecondary, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{txt}</div>
          <WaHistorialAudio item={item} />
        </>
      );
    }

    const lineas = txt.split('\n').filter(l => l.trim() !== '');
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {lineas.map((l, i) => {
          const esAna = /^\[(Ana|Agent)\]:/i.test(l) || /^(Ana|Agent):/i.test(l);
          const esLead = /^\[(Lead|Cliente|Usuario|User)\]:/i.test(l) || /^(Lead|Cliente|Usuario|User):/i.test(l);
          if (!esAna && !esLead) {
            return <div key={i} style={{ fontSize: 12, color: c.textMuted, fontStyle: 'italic' }}>{l}</div>;
          }
          const autor = esAna ? 'Ana' : 'Cliente';
          const mensaje = l.replace(/^\[(Ana|Agent|Lead|Cliente|Usuario|User)\]:\s*/i, '').replace(/^(Ana|Agent|Lead|Cliente|Usuario|User):\s*/i, '');
          return (
            <div key={i} style={{ display: 'flex', flexDirection: esAna ? 'row' : 'row-reverse', gap: 8, alignItems: 'flex-start' }}>
              <div style={{
                flexShrink: 0, width: 28, height: 28, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: esAna ? 'linear-gradient(135deg, #7c3aed, #6d28d9)' : (isDark ? '#334155' : '#e2e8f0'),
                fontSize: 10, fontWeight: 700, color: esAna ? '#fff' : c.textPrimary,
              }}>{autor[0]}</div>
              <div style={{
                maxWidth: '75%',
                padding: '8px 12px',
                borderRadius: esAna ? '4px 12px 12px 12px' : '12px 4px 12px 12px',
                background: esAna
                  ? (isDark ? 'rgba(109,40,217,0.18)' : 'rgba(109,40,217,0.08)')
                  : (isDark ? '#1e293b' : '#f1f5f9'),
                fontSize: 13,
                color: c.textPrimary,
                lineHeight: 1.5,
              }}>{mensaje}</div>
            </div>
          );
        })}
      </div>
    );
  };

  const historialFiltrado = useMemo(
    () => historial.filter((h) => historialPasaFiltros(h, historialFiltrosIncl, historialFiltrosExcl)),
    [historial, historialFiltrosIncl, historialFiltrosExcl],
  );

  const abrirWhatsApp = () => {
    if (soloLectura || !contactoWhatsApp?.telefono) return;
    setActiveTaskModal('whatsapp');
  };

  const abrirEmail = () => {
    if (soloLectura || !lead?.id) return;
    setActiveTaskModal('email');
  };

  const marcarWhatsappEnviadoYRecargar = async () => {
    if (!lead?.id) return;
    try {
      await api.post(`/tareas/lead/${lead.id}/whatsapp-enviado`).catch(() => {});
      await recargarPanelTareaHistorial();
    } catch {
      /* ignorar */
    }
  };

  const crearClienteDesdeLead = async () => {
    if (!lead?.id || soloLectura || lead.cliente_id) return;
    setCreandoCliente(true);
    try {
      const res = await api.post(`/leads/${lead.id}/crear-cliente`);
      if (res.data?.ok) {
        onLeadRefresh?.(res.data.lead);
        const cid = res.data.cliente?.id;
        if (cid) {
          const ir = await dialogs.confirm(
            res.data.ya_existia
              ? 'El lead ya estaba vinculado al cliente. ¿Abrir la ficha del cliente?'
              : 'Cliente y demanda creados. ¿Abrir la ficha para completar datos?',
          );
          if (ir) navigate(`/clientes/${cid}`);
        }
      }
    } catch (err) {
      await dialogs.alert(err.response?.data?.error || 'No se pudo crear el cliente');
    } finally {
      setCreandoCliente(false);
    }
  };

  useEffect(() => {
    if (!lead?.id || soloLectura) return undefined;
    const tick = () => { recargarPanelTareaHistorial(); };
    tick();
    const interval = setInterval(tick, 10000);
    const onFocus = () => tick();
    window.addEventListener('focus', onFocus);
    return () => {
      clearInterval(interval);
      window.removeEventListener('focus', onFocus);
    };
  }, [lead?.id, soloLectura]);

  useEffect(() => {
    setTareaModalDescartado(false);
  }, [lead?.id]);

  useEffect(() => {
    // Solo gestiona la auto-apertura de M1/M2. No pisa modales abiertos manualmente
    // (whatsapp/agente): si hay uno de esos activo, no lo cierra aquí.
    const closeAutoModals = () =>
      setActiveTaskModal((prev) => (prev === 'cualificacion' || prev === 'visita' ? null : prev));

    if (soloLectura) {
      closeAutoModals();
      return;
    }
    // Si el agente cerró manualmente ESTA tarea en esta sesión, no volver a
    // auto-abrirla aunque el polling refresque (evita el bucle "cierro y vuelve").
    // Una tarea DISTINTA sí abre su modal (el flujo avanza). Al re-entrar al lead
    // el ref se reinicia y, si sigue pendiente, vuelve a salir.
    if (tareaVigente?.id && dismissedTaskIdsRef.current?.has(tareaVigente.id)) {
      closeAutoModals();
      return;
    }
    // No auto-abrir modales para tareas vencidas: deben resolverse por el flujo
    // (reasignación a Ana / paso a Fríos), no reabrir un modal en bucle en cada poll.
    if (tareaVigente && isLeadTaskExpired(tareaVigente)) {
      closeAutoModals();
      return;
    }
    // En espera de respuesta SIN respuesta aún: no auto-abrir nada. Cuando llega la
    // respuesta (reply pending) se abre M1 (T3b, T4b2/b3) o M2 (T4, T4b1).
    // Excepción: T4b1 abierta a mano con «Abrir tarea».
    const manualT4b1 =
      manualM2T4b1TaskIdRef.current &&
      String(manualM2T4b1TaskIdRef.current) === String(tareaVigente?.id || '');
    if (isWhatsappWaitingTask(tareaVigente) && !hasWhatsappReplyPending(tareaVigente) && !manualT4b1) {
      closeAutoModals();
      return;
    }
    if (taskNeedsM1Modal(tareaVigente)) {
      setActiveTaskModal('cualificacion');
      return;
    }
    if (taskNeedsM2Modal(tareaVigente) || manualT4b1) {
      setActiveTaskModal('visita');
      return;
    }
    closeAutoModals();
  }, [tareaVigente, soloLectura, tareaModalDescartado]);
  const abrirLlamada  = async () => {
    const telefonoNorm = String(lead?.telefono || '').trim().replace(/\s+/g, '');
    if (soloLectura || !telefonoNorm) return;
    try {
      const data = await api.post('/telnyx/llamar', {
        telefonoCliente: telefonoNorm,
        clienteId: lead?.cliente_id || null,
        leadId: lead?.id || null,
        vincularCitaPendiente: false,
      }).then((r) => r.data);
      if (data?.ok) {
        await dialogs.alert('📞 Llamada iniciada — tu móvil recibirá la llamada en breve', { autoCloseMs: 2000 });
        const leadId = lead?.id;
        [10000, 20000, 35000, 60000, 90000].forEach((ms) => {
          setTimeout(() => recargarPanelTareaHistorial(), ms);
        });
        // Respaldo si el webhook de Telnyx no llega: inferir outcome con BD + API Telnyx
        const runBridgeBackup = async () => {
          if (!leadId) return;
          await api.post('/telnyx/cerrar-llamada-bridge', { leadId, backup: true }).catch(() => {});
          await recargarPanelTareaHistorial();
        };
        setTimeout(runBridgeBackup, 125000);
        setTimeout(runBridgeBackup, 300000);
      } else {
        await dialogs.alert(`Error: ${data?.error || 'No se pudo iniciar la llamada'}`);
      }
    } catch (e) {
      await dialogs.alert(`Error: ${e.response?.data?.error || e.message}`);
    }
  };

  const abrirTareaVigente = async () => {
    setTareaModalDescartado(false);
    if (tareaVigente?.id) dismissedTaskIdsRef.current.delete(tareaVigente.id);
    if (!tareaVigente?.id) return;
    if (isLeadTaskExpired(tareaVigente)) {
      await dialogs.alert('El plazo de esta tarea ha vencido. El lead debería haber pasado a Fríos o Eliminados según el flujo. Recarga el panel para ver el estado actual.');
      await recargarPanelTareaHistorial();
      return;
    }
    if (taskIsM1Type(tareaVigente)) {
      let tareaFresh = tareaVigente;
      if (lead?.id) {
        try {
          await api.post(`/tareas/lead/${lead.id}/reabrir-modal`, { tarea_id: tareaVigente.id });
          const r = await api.get(`/tareas/lead/${lead.id}/activa`);
          if (r.data?.tarea) tareaFresh = r.data.tarea;
        } catch { /* usa tarea original */ }
      }
      setTareaVigente(tareaFresh);
      setActiveTaskModal('cualificacion');
      return;
    }
    if (taskIsM2Type(tareaVigente)) {
      setActiveTaskModal('visita');
      return;
    }
    if (isT4b1AutoContactTask(tareaVigente) || isT4b1Task(tareaVigente)) {
      manualM2T4b1TaskIdRef.current = tareaVigente.id;
      setActiveTaskModal('visita');
      return;
    }
    if (isAgenteResultTask(tareaVigente) && !isWhatsappWaitingTask(tareaVigente)) {
      setActiveTaskModal('agente');
      return;
    }
    if (lead?.id && (isWhatsappWaitingTask(tareaVigente) || isWhatsappFlowTask(tareaVigente))) {
      const code = String(parseTaskMetadata(tareaVigente).task_code || '').toUpperCase();
      const isVisitWa = ['T3B', 'T3B1'].includes(code);
      const isT4bObjecionesDescarte = isT4bObjecionesDescarteTask(tareaVigente);
      const ok = await dialogs.confirm(
        isWhatsappWaitingTask(tareaVigente)
          ? (isT4bObjecionesDescarte
            ? '¿El cliente ha respondido? Se abrirá el resultado del contacto.'
            : isVisitWa
              ? '¿El lead ha respondido al WhatsApp? Se abrirá el modal para agendar cita.'
              : '¿El lead ha respondido al WhatsApp? Se abrirá la cualificación.')
          : '¿Has enviado el WhatsApp? Marca la respuesta del lead para continuar.',
      );
      if (!ok) return;
      try {
        await api.post(`/tareas/lead/${lead.id}/whatsapp-respondido`);
        setTareaModalDescartado(false);
        if (tareaVigente?.id) dismissedTaskIdsRef.current.delete(tareaVigente.id);
        await recargarPanelTareaHistorial();
        const r = await api.get(`/tareas/lead/${lead.id}/activa`);
        const tActualizada = r.data?.tarea || null;
        if (tActualizada) setTareaVigente(tActualizada);
        if (taskNeedsM1Modal(tActualizada)) {
          setActiveTaskModal('cualificacion');
        } else if (taskIsM2Type(tActualizada)) {
          setActiveTaskModal('visita');
        }
      } catch (err) {
        await dialogs.alert(err.response?.data?.error || 'No se pudo registrar la respuesta');
      }
      return;
    }
    // T4/TC · Cita: ir a la ficha del cliente y abrir el modal de la cita.
    {
      const codeVisita = String(parseTaskMetadata(tareaVigente).task_code || '').toUpperCase();
      const agendaIdVisita = getVisitAgendaId(tareaVigente);
      if (
        agendaIdVisita &&
        (isVisitFeedbackTask(tareaVigente) || codeVisita === 'TC' || codeVisita === 'T4')
      ) {
        const clienteId = lead?.cliente_id || tareaVigente?.cliente_id;
        if (clienteId) {
          onClose?.();
          navigate(`/clientes/${clienteId}`, {
            state: { abrirTareaVigente: true, tareaId: tareaVigente.id },
          });
          return;
        }
      }
    }
    navigate('/tareas');
  };

  const accionBtn = (label, icono, color, onClick) => (
    <button key={label} type="button" onClick={soloLectura ? undefined : onClick} disabled={soloLectura} style={{
      flex: 1, padding: '8px 4px', borderRadius: 8, border: `1px solid ${c.border}`,
      background: c.bgInput, color: c.textSecondary,
      cursor: soloLectura ? 'not-allowed' : 'pointer', fontFamily: 'inherit', fontSize: 11, fontWeight: 600,
      display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3,
      transition: 'all 0.15s', opacity: soloLectura ? 0.45 : 1,
    }}
      onMouseEnter={e => { if (soloLectura) return; e.currentTarget.style.borderColor = color; e.currentTarget.style.color = color; e.currentTarget.style.background = `${color}10`; }}
      onMouseLeave={e => { if (soloLectura) return; e.currentTarget.style.borderColor = c.border; e.currentTarget.style.color = c.textSecondary; e.currentTarget.style.background = c.bgInput; }}
    >
      <span style={{ fontSize: 16, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{icono}</span>
      <span>{label}</span>
    </button>
  );

  return (
    <div style={{ flex: 1, minWidth: 320, borderLeft: `1px solid ${c.border}`, display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: 0 }}>

      {/* Info del lead */}
      <div style={{ padding: '14px 16px 10px', borderBottom: `1px solid ${c.border}`, flexShrink: 0 }}>
        {lead && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: c.textPrimary }}>{lead.nombre} {lead.apellido}</div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {lead.etapa && (
                <span style={{ padding: '2px 8px', borderRadius: 10, fontSize: 10, fontWeight: 700, background: ml.blueBg, color: ml.chipText, border: `1px solid ${ml.blueBorder}` }}>
                  {lead.etapa.replace(/_/g, ' ').toUpperCase()}
                </span>
              )}
              <span
                title="Idioma para WhatsApp y ficha cliente"
                style={{ padding: '2px 8px', borderRadius: 10, fontSize: 10, fontWeight: 700, background: 'rgba(16,185,129,0.12)', color: '#059669', border: '1px solid rgba(16,185,129,0.3)' }}
              >
                {formatIdiomaBadge(idiomaWhatsApp)}
              </span>
            </div>
            {lead.agente_nombre && <div style={{ fontSize: 11, color: c.textPrimary }}>👤 {lead.agente_nombre}</div>}
            {(lead.tipo_interes || lead.poblacion_interes || lead.habitaciones_min) && (
              <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', marginTop: 4 }}>
                {lead.tipo_interes && <span style={{ padding: '2px 7px', borderRadius: 10, fontSize: 10, fontWeight: 700, background: 'rgba(37,99,235,0.10)', color: '#ffffff', border: '1px solid rgba(37,99,235,0.25)' }}>Tipo: {lead.tipo_interes}</span>}
                {lead.poblacion_interes && <span style={{ padding: '2px 7px', borderRadius: 10, fontSize: 10, fontWeight: 700, background: 'rgba(16,185,129,0.10)', color: '#059669', border: '1px solid rgba(16,185,129,0.25)' }}>Población: {lead.poblacion_interes}</span>}
                {lead.habitaciones_min && <span style={{ padding: '2px 7px', borderRadius: 10, fontSize: 10, fontWeight: 700, background: 'rgba(245,158,11,0.10)', color: '#d97706', border: '1px solid rgba(245,158,11,0.25)' }}>{lead.habitaciones_min} dorm.</span>}
              </div>
            )}
            {(() => {
              const ex = parseDatosExtras(lead);
              const rel = ex?.relacion_cliente;
              if (rel === 'cliente_activo' || rel === 'cliente_frios') {
                const texto = rel === 'cliente_frios'
                  ? 'CLIENTE ASIGNADO DESDE FRÍOS. Revisa su ficha completa antes de llamarle.'
                  : 'CLIENTE EXISTENTE. Revisa su ficha completa antes de llamarle.';
                return (
                  <div style={{ marginTop: 6, padding: '9px 12px', borderRadius: 8, fontSize: 11.5, fontWeight: 800, lineHeight: 1.45, background: isDark ? 'rgba(220,38,38,0.16)' : '#fee2e2', color: isDark ? '#fecaca' : '#991b1b', border: `2px solid ${isDark ? 'rgba(220,38,38,0.5)' : '#dc2626'}` }}>
                    ⚠️ {texto}
                  </div>
                );
              }
              const yaAlta = ex?.cliente_ya_de_alta === true || (Boolean(lead.cliente_id) && Boolean(ex?.cliente_coincidente_id));
              if (!yaAlta) return null;
              return (
                <div style={{ marginTop: 6, padding: '9px 12px', borderRadius: 8, fontSize: 11.5, fontWeight: 700, lineHeight: 1.45, background: isDark ? 'rgba(245,158,11,0.14)' : '#fffbeb', color: isDark ? '#fcd34d' : '#92400e', border: `1px solid ${isDark ? 'rgba(245,158,11,0.4)' : '#fde68a'}` }}>
                  ⚠️ Cliente ya de alta. Revisa todo su historial antes de llamarle.
                </div>
              );
            })()}
            {lead.created_at   && <div style={{ fontSize: 10, color: c.textMuted }}>📅 Alta: {formatFecha(lead.created_at)}</div>}
            {verVerCliente && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 6 }}>
              {lead.cliente_id ? (
                <button
                  type="button"
                  onClick={() => { if (!soloLectura) navigate(`/clientes/${lead.cliente_id}`); }}
                  disabled={soloLectura}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '5px 12px', borderRadius: 8, fontSize: 11, fontWeight: 700, background: 'rgba(37,99,235,0.12)', color: '#2563eb', border: '1px solid rgba(37,99,235,0.35)', cursor: soloLectura ? 'not-allowed' : 'pointer', fontFamily: 'inherit', transition: 'all 0.15s', opacity: soloLectura ? 0.5 : 1 }}
                  onMouseEnter={e => { if (!soloLectura) e.currentTarget.style.background = 'rgba(37,99,235,0.22)'; }}
                  onMouseLeave={e => { if (!soloLectura) e.currentTarget.style.background = 'rgba(37,99,235,0.12)'; }}>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                  Ver ficha del cliente
                </button>
              ) : (
                <button
                  type="button"
                  onClick={crearClienteDesdeLead}
                  disabled={soloLectura || creandoCliente}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '5px 12px', borderRadius: 8, fontSize: 11, fontWeight: 700, background: 'rgba(16,185,129,0.12)', color: '#059669', border: '1px solid rgba(16,185,129,0.35)', cursor: soloLectura || creandoCliente ? 'not-allowed' : 'pointer', fontFamily: 'inherit', opacity: soloLectura ? 0.5 : 1 }}
                >
                  {creandoCliente ? 'Creando…' : '➕ Crear cliente'}
                </button>
              )}
            </div>
            )}
            {verAbrirTarea && (
            <>
            <div style={{ fontSize: 10, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.05em', color: isDark ? c.textPrimary : '#1d4ed8', marginTop: 8, marginBottom: 4 }}>
              Tarea vigente
            </div>
            {panelCargado && !tareaVigente && !soloLectura ? (
              <AsignarTareaFlujoBlock
                options={taskOptions}
                value=""
                disabled={asignandoTarea}
                highlightError={Boolean(errorAsignarTarea)}
                c={c}
                isDark={isDark}
                onSelect={async (code) => {
                  if (!lead?.id || !code) return;
                  // T4·Cita / T3: ModalCita antes de asignar (si cancela, no queda T3 huérfana).
                  if (taskRequiereCitaAntesDeAlta(code)) {
                    onPedirModalCita?.({ lead, pendingTaskCode: code });
                    return;
                  }
                  setAsignandoTarea(true);
                  setErrorAsignarTarea('');
                  try {
                    const res = await api.post(`/leads/${lead.id}/asignar-tarea-flujo`, { task_code: code });
                    if (res.data?.ok) {
                      if (res.data.tarea) setTareaVigente(res.data.tarea);
                      if (res.data.data) onLeadRefresh?.(res.data.data);
                      await recargarPanelTareaHistorial?.();
                    }
                  } catch (err) {
                    setErrorAsignarTarea(
                      err.response?.data?.mensaje || err.response?.data?.error || MSG_DEBE_ASIGNAR_TAREA
                    );
                  } finally {
                    setAsignandoTarea(false);
                  }
                }}
              />
            ) : (
              <TaskCurrentCard
                task={tareaVigente}
                leadEtapa={lead?.etapa}
                leadCreatedAt={lead?.created_at || lead?.fecha_alta || null}
                c={c}
                isDark={isDark}
                variant="leadModal"
                loading={!panelCargado && !tareaVigente}
                onOpen={abrirTareaVigente}
              />
            )}
            {errorAsignarTarea && (
              <div style={{ marginTop: 6, fontSize: 11, fontWeight: 600, color: '#ef4444' }}>{errorAsignarTarea}</div>
            )}
            </>
            )}
            {panelCargado && !tareaVigente && (() => {
              let extras = lead?.datos_extras;
              if (typeof extras === 'string') {
                try { extras = JSON.parse(extras); } catch { extras = {}; }
              }
              const until = extras?.qualification_deferred_until;
              if (!until) return null;
              const d = new Date(until);
              const label = Number.isNaN(d.getTime())
                ? until
                : d.toLocaleString('es-ES', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
              return (
                <div style={{ marginTop: 8, padding: '8px 10px', borderRadius: 8, fontSize: 11, fontWeight: 600, lineHeight: 1.45, background: isDark ? 'rgba(245,158,11,0.12)' : '#fffbeb', color: isDark ? '#fcd34d' : '#92400e', border: `1px solid ${isDark ? 'rgba(245,158,11,0.35)' : '#fde68a'}` }}>
                  Sin tarea asignada. Indica el resultado del contacto antes del {label} o el lead pasará a Eliminados.
                </div>
              );
            })()}
            {soloLectura && (
              <div style={{ marginTop: 8, padding: '8px 10px', borderRadius: 8, fontSize: 11, fontWeight: 600, lineHeight: 1.45, background: isDark ? 'rgba(239,68,68,0.12)' : '#fef2f2', color: isDark ? '#fca5a5' : '#991b1b', border: `1px solid ${isDark ? 'rgba(239,68,68,0.35)' : '#fecaca'}` }}>
                Lead en Eliminados: solo lectura. Restaura el lead desde la papelera para volver a actuar o enviar automatizaciones.
              </div>
            )}
          </div>
        )}
      </div>

      {/* Acciones rápidas */}
      <div style={{ padding: '12px 16px', borderBottom: `1px solid ${c.border}`, flexShrink: 0 }}>
        <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: c.textMuted, marginBottom: 8 }}>Acciones rápidas</div>
        <div style={{ display: 'flex', gap: 6 }}>
          {verAccionWhatsapp && accionBtn('WhatsApp', <IconoWhatsApp />,  WA_VERDE_OFICIAL, abrirWhatsApp)}
          {verAccionEmail && accionBtn('Email',    '✉️',               '#3b82f6', abrirEmail)}
          {verAccionLlamada && accionBtn('Llamada',  '📞',               '#f59e0b', abrirLlamada)}
          {verAccionNota && accionBtn('Nota',     '📝',               '#a855f7', () => { if (!soloLectura) setMostrarNota(v => !v); })}
        </div>
        {verAccionNota && mostrarNota && !soloLectura && (
          <div style={{ marginTop: 8 }}>
            <textarea value={notaTexto} onChange={e => setNotaTexto(e.target.value)}
              placeholder="Escribe una nota rápida..." rows={3}
              style={{ width: '100%', padding: '7px 10px', fontSize: 12, borderRadius: 8, border: `1px solid ${c.border}`, background: c.bgInput, color: c.textPrimary, fontFamily: 'inherit', resize: 'none', outline: 'none', boxSizing: 'border-box' }} />
            <button type="button" onClick={() => {
              if (!notaTexto.trim()) return;
              api.post(`/leads/${lead.id}/notas`, { texto: notaTexto })
                .then(() => { setNotaTexto(''); setMostrarNota(false); api.get(`/leads/${lead.id}/historial`).then(r => setHistorial(r.data?.data || [])).catch(() => {}); })
                .catch(() => {});
            }} style={{ marginTop: 4, width: '100%', padding: '6px', borderRadius: 7, border: 'none', background: ml.blue, color: '#fff', fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
              Guardar nota
            </button>
          </div>
        )}
      </div>

      {/* Filtros historial — igual que FichaCliente: click incluir, doble click excluir */}
      {verHistorial && (
      <div style={{ padding: '10px 16px 8px', borderBottom: `1px solid ${c.border}`, flexShrink: 0 }}>
        <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: c.textMuted, marginBottom: 8 }}>Historial</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
          {[
            { id: 'todos', label: 'Todos', reset: true },
            { id: 'whatsapp', label: 'WhatsApp' },
            { id: 'mail', label: 'Mail' },
            { id: 'llamadas', label: 'Llamadas' },
            { id: 'citas', label: 'Citas' },
            { id: 'notas', label: 'Notas' },
            { id: 'sistema', label: 'Sistema' },
          ].map((f) => {
            const active = f.reset
              ? historialSinFiltroActivo
              : historialFiltrosIncl.includes(f.id);
            const excluded = !f.reset && historialFiltrosExcl.includes(f.id);
            return (
              <HistorialFiltroChip
                key={f.id}
                label={f.label}
                active={active}
                excluded={excluded}
                isDark={isDark}
                c={c}
                onClick={() => onHistorialFiltroChipPress(f.id, !!f.reset)}
              />
            );
          })}
        </div>
      </div>
      )}

      {/* Lista historial */}
      {verHistorial && (
      <div style={{ flex: 1, overflowY: 'auto', padding: '10px 16px' }}>
        {cargando && <div style={{ fontSize: 11, color: c.textMuted, textAlign: 'center', marginTop: 20 }}>Cargando...</div>}

        {!cargando && historialFiltrado.length === 0 && (
          <div style={{ fontSize: 11, color: c.textMuted, textAlign: 'center', marginTop: 20, lineHeight: 1.8 }}>Sin actividad registrada</div>
        )}

        {!cargando && historialFiltrado.map((item, i) => {
          const meta = metaTipo(item);
          const fecha = item.created_at ? new Date(item.created_at) : null;
          const fechaStr = fecha ? fecha.toLocaleDateString('es-ES', { day: '2-digit', month: 'short' }) : '—';
          const horaStr = fecha ? fecha.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' }) : '';
          const texto = textoItem(item);
          const esWaPlantilla =
            item.tipo === 'whatsapp_enviado' || item.tipo === 'whatsapp_recibido';
          const mostrarLupa = esWaPlantilla || String(texto || '').length > 120;
          return (
            <div key={item.id || i} style={{
              display: 'flex',
              gap: 10,
              padding: isDark ? '8px 0' : '9px 10px',
              background: isDark ? 'transparent' : c.bgCard,
              border: isDark ? 'none' : `1px solid ${c.border}`,
              borderLeft: isDark ? 'none' : `3px solid ${meta.accent}`,
              borderRadius: isDark ? 0 : 8,
              borderBottom: isDark ? `1px solid ${c.border}` : undefined,
              marginBottom: isDark ? 0 : 4,
              boxShadow: isDark ? 'none' : '0 1px 3px rgba(0,0,0,0.05)',
            }}>
              <div style={{
                width: 30, height: 30, borderRadius: 7, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                background: meta.bg, boxShadow: meta.shadow,
              }}>{meta.icon}</div>

              <div style={{ flexShrink: 0, width: 44, textAlign: 'center', paddingTop: 1 }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: c.textPrimary, lineHeight: 1.2 }}>{fechaStr}</div>
                <div style={{ fontSize: 10, color: c.textMuted, lineHeight: 1.2 }}>{horaStr}</div>
              </div>

              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: c.textPrimary, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <PuntoWaHistorial tipo={item.tipo} />
                  <span>{meta.label}</span>
                </div>
                <div style={{ fontSize: 11, color: c.textMuted, marginTop: 2 }}>{autorItem(item)}</div>
                {texto && (
                  <div style={{ fontSize: 11, color: c.textSecondary, marginTop: 4, lineHeight: 1.4, display: 'flex', alignItems: 'flex-start', gap: 4 }}>
                    <span style={{ flex: 1, minWidth: 0 }}>
                      <span style={{ fontWeight: 600, color: c.textMuted }}>Texto: </span>
                      {texto.substring(0, 120)}{texto.length > 120 ? '...' : ''}
                      <WaHistorialAudio item={item} />
                    </span>
                    {mostrarLupa && (
                      <button
                        onClick={() => setVisorHistorial({
                          ...item,
                          contenido: esWaPlantilla
                            ? textoCompletoHistorialWa(item, parseHistorialMetadata(item))
                            : item.contenido,
                        })}
                        title="Ver conversación completa"
                        style={{
                          flexShrink: 0, background: 'none', border: 'none', cursor: 'pointer',
                          padding: '2px 4px', borderRadius: 4, color: c.textMuted,
                          display: 'flex', alignItems: 'center',
                        }}
                      >
                        <svg width={13} height={13} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
                          <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                        </svg>
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
      )}

      {modalWhatsAppOpen && contactoWhatsApp && (
        <ModalWhatsApp
          isOpen
          contact={contactoWhatsApp}
          idioma={idiomaWhatsApp}
          crmContext={{
            lead_id: lead.id,
            cliente_id: lead.cliente_id || null,
          }}
          onClose={() => {
            setActiveTaskModal(null);
            setTareaModalDescartado(true);
            if (tareaVigente?.id) dismissedTaskIdsRef.current.add(tareaVigente.id);
          }}
          onSend={async () => {
            setActiveTaskModal(null);
            await marcarWhatsappEnviadoYRecargar();
          }}
        />
      )}

      {modalEmailOpen && contactoEmail && (
        <ModalEmail
          isOpen
          contact={contactoEmail}
          allowManualRecipient={!emailResuelto}
          idioma={idiomaWhatsApp}
          crmContext={{
            lead_id: lead.id,
            cliente_id: lead.cliente_id || null,
          }}
          onClose={() => {
            setActiveTaskModal(null);
            setTareaModalDescartado(true);
            if (tareaVigente?.id) dismissedTaskIdsRef.current.add(tareaVigente.id);
          }}
          onSend={async () => {
            setActiveTaskModal(null);
            await recargarPanelTareaHistorial();
          }}
        />
      )}

      {modalCualificacionOpen && taskIsM1Type(tareaVigente) && (
        <ModalResultadoCualificacion
          isOpen={modalCualificacionOpen}
          onClose={() => {
            setActiveTaskModal(null);
            setTareaModalDescartado(true);
            if (tareaVigente?.id) dismissedTaskIdsRef.current.add(tareaVigente.id);
          }}
          lead={lead}
          tarea={tareaVigente}
          theme={{ name: isDark ? 'dark' : 'light', colors: c }}
          onCompleted={async (data) => {
            await recargarPanelPostLlamada();
            setActiveTaskModal(null);
            const shouldOpenWhatsApp =
              data?.mode === "enviar_propiedad" ||
              ["T1C", "T2E", "T3B", "T3B1"].includes(
                String(parseTaskMetadata(data?.nextTask).task_code || "").toUpperCase()
              );
            if (!shouldOpenWhatsApp || !lead?.id) return;
            try {
              const r = await api.get(`/tareas/lead/${lead.id}/activa`);
              const t = r.data?.tarea || null;
              const code = String(parseTaskMetadata(t).task_code || "").toUpperCase();
              if (!["T1C", "T2E", "T3B", "T3B1"].includes(code) || isWhatsappWaitingTask(t)) return;
              setTareaModalDescartado(false);
              if (t?.id) dismissedTaskIdsRef.current.delete(t.id);
              setTareaVigente(t);
              setActiveTaskModal("whatsapp");
            } catch {
              /* ignorar */
            }
          }}
          onLlamarTelefono={abrirLlamada}
        />
      )}

      {modalResultadoVisitaOpen && (taskIsM2Type(tareaVigente) || isT4b1Task(tareaVigente)) && (
        <ModalResultadoVisita
          isOpen={modalResultadoVisitaOpen}
          tarea={tareaVigente}
          theme={{ name: isDark ? 'dark' : 'light', colors: c }}
          onClose={() => {
            manualM2T4b1TaskIdRef.current = null;
            setActiveTaskModal(null);
            setTareaModalDescartado(true);
            if (tareaVigente?.id) dismissedTaskIdsRef.current.add(tareaVigente.id);
          }}
          onCompleted={async () => {
            manualM2T4b1TaskIdRef.current = null;
            setActiveTaskModal(null);
            await recargarPanelPostLlamada();
          }}
        />
      )}

      {modalResultadoAgenteOpen && isAgenteResultTask(tareaVigente) && (
        <ModalResultadoTareaAgente
          isOpen={modalResultadoAgenteOpen}
          tarea={tareaVigente}
          theme={{ name: isDark ? 'dark' : 'light', colors: c }}
          onClose={() => {
            setActiveTaskModal(null);
            setTareaModalDescartado(true);
            if (tareaVigente?.id) dismissedTaskIdsRef.current.add(tareaVigente.id);
          }}
          onCompleted={async () => {
            setActiveTaskModal(null);
            await recargarPanelPostLlamada();
          }}
        />
      )}

      {visorHistorial && (
        <div onClick={() => setVisorHistorial(null)} style={{
          position: 'fixed', inset: 0, zIndex: 9999,
          background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <div onClick={e => e.stopPropagation()} style={{
            background: isDark ? '#1e293b' : '#fff',
            borderRadius: 16, padding: '24px 24px 20px', width: '90%', maxWidth: 560,
            maxHeight: '80vh', display: 'flex', flexDirection: 'column', gap: 16,
            boxShadow: '0 20px 60px rgba(0,0,0,0.35)',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <div style={{ fontSize: 14, fontWeight: 700, color: c.textPrimary }}>
                  {visorHistorial.tipo === 'llamada_ana' ? '🤖 Llamada Ana (IA)' : metaTipo(visorHistorial).label}
                  {visorHistorial.asunto ? ` — ${visorHistorial.asunto}` : ''}
                </div>
                {visorHistorial.created_at && (
                  <div style={{ fontSize: 11, color: c.textMuted, marginTop: 2 }}>
                    {new Date(visorHistorial.created_at).toLocaleString('es-ES', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                  </div>
                )}
              </div>
              <button onClick={() => setVisorHistorial(null)} style={{
                background: 'none', border: 'none', cursor: 'pointer', color: c.textMuted, padding: 4, borderRadius: 6,
              }}>
                <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                  <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            </div>

            <div style={{ flex: 1, overflowY: 'auto', paddingRight: 4 }}>
              {renderConversacionCompleta(visorHistorial)}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function telefonoListoParaDup(v) {
  return String(v || '').replace(/\D/g, '').length >= 9;
}

function emailListoParaDup(v) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(v || '').trim());
}

/** Misma clasificación que leads.controller (Activos / Fríos Contactos). */
function bucketClienteEstadoAlta(c) {
  if (!c || c.activo === false) return 'eliminados';
  const e = String(c.estado || '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
  if (['frio', 'frios', 'inactivo'].includes(e)) return 'frios';
  if (!e || ['activo', 'potencial', 'contactado', 'ventas'].includes(e)) return 'activos';
  return 'otro';
}

function nombreCompletoClienteDup(c) {
  return `${c?.nombre || ''} ${c?.apellido || ''}`.trim() || 'Cliente';
}

/** Mapea GET /clientes/buscar-duplicado → mismos paneles que el 409 al guardar. */
function mapBuscarDuplicadoAConfirm(data, formSnap = {}) {
  const cli = data?.cliente_existente;
  if (cli?.id) {
    const bucket = bucketClienteEstadoAlta(cli);
    const agNom = cli.agente_nombre || 'sin agente asignado';
    const nombreNuevo = `${formSnap.nombre || ''} ${formSnap.apellido || ''}`.trim();
    const base = {
      permite_crear_aparte: bucket !== 'activos' && bucket !== 'frios',
      cliente_existente: {
        id: cli.id,
        nombre: nombreCompletoClienteDup(cli),
        email: cli.email || null,
        telefono: cli.telefono || null,
        telefono_secundario: cli.telefono_secundario || null,
        agente_id: cli.agente_id || null,
        agente_nombre: cli.agente_nombre || null,
        estado: cli.estado || null,
        en_activos: bucket === 'activos',
        en_frios: bucket === 'frios',
      },
      datos_nuevos: {
        nombre: nombreNuevo,
        email: formSnap.email || null,
        telefono: formSnap.telefono || null,
      },
      agente_nombre: cli.agente_nombre || null,
      duplicate_type: data.duplicate_type || null,
      reason: data.reason || null,
    };
    if (bucket === 'frios') {
      return {
        kind: 'frios',
        payload: {
          ...base,
          error: 'cliente_en_frios_requiere_cita',
          mensaje:
            `Este teléfono está en Fríos (agente ${agNom}). ` +
            'No se puede guardar el lead sin crear una cita.',
        },
      };
    }
    if (bucket === 'activos') {
      return {
        kind: 'cliente',
        payload: {
          ...base,
          error: 'conflicto_cliente_activo',
          mensaje:
            `Este teléfono está en Activos con el agente ${agNom}. ` +
            'Puedes crear el lead: se añadirá a ese cliente y solo será visible para ese agente; ' +
            'no se puede asignar a otro agente.',
        },
      };
    }
    return {
      kind: 'cliente',
      payload: {
        ...base,
        error: 'conflicto_cliente',
        mensaje: data.mensaje || data.reason || 'Este teléfono o email ya existe en otro cliente.',
      },
    };
  }

  const leadSuelto = data?.lead_suelto;
  if (leadSuelto?.id) {
    return {
      kind: 'lead_suelto',
      payload: {
        error: 'conflicto_lead_suelto',
        mensaje:
          'Ya existe un lead sin ficha de cliente con el mismo teléfono o email. Puedes abrir el lead existente o cambiar la consulta.',
        lead_existente: {
          id: leadSuelto.id,
          nombre: `${leadSuelto.nombre || ''} ${leadSuelto.apellido || ''}`.trim(),
          email: leadSuelto.email,
          telefono: leadSuelto.telefono,
          etapa: leadSuelto.etapa,
          fuente: leadSuelto.fuente,
        },
        duplicate_type: data.duplicate_type || null,
        reason: data.reason || null,
      },
    };
  }
  return null;
}

// ── Modal principal ─────────────────────────────────────────
export default function ModalNuevoLead({ isOpen, onClose, onCreated, onLeadUpdated, lead }) {
  const { theme } = useTheme();
  const c = theme.colors;
  const isDark = theme.name === 'dark';
  const { usuario } = useAuth();
  const { puede } = usePermiso();
  const puedeCrearIgualmenteExacto = puedeCrearLeadConClienteDuplicadoExacto(usuario);
  const tituloSinPermiso = 'No tienes permiso para modificar este campo';
  const [taskOptions, setTaskOptions] = useState(LEAD_FLOW_TASK_OPTIONS_FALLBACK);
  const [taskCodeInicial, setTaskCodeInicial] = useState('');
  const [errorTareaInicial, setErrorTareaInicial] = useState(false);
  /**
   * ModalCita ANTES de crear el lead (TC / T3 / T3AE).
   * { draft: true, propiedades, agentName, pendingOpts } — aún no hay lead.
   */
  const [modalCitaAlta, setModalCitaAlta] = useState(null);
  const [guardandoCitaAlta, setGuardandoCitaAlta] = useState(false);
  const pendingAltaOptsRef = useRef({});
  // Campos de la ficha del lead (ver+modificar cada uno).
  const verNombre = puede('pl_leads_campo_nombre', 'ver');
  const modNombre = puede('pl_leads_campo_nombre', 'modificar');
  const verTelefono = puede('pl_leads_campo_telefono', 'ver');
  const modTelefono = puede('pl_leads_campo_telefono', 'modificar');
  const verEmail = puede('pl_leads_campo_email', 'ver');
  const modEmail = puede('pl_leads_campo_email', 'modificar');
  const verIdioma = puede('pl_leads_campo_idioma', 'ver');
  const modIdioma = puede('pl_leads_campo_idioma', 'modificar');
  const verCampoAgente = puede('pl_leads_campo_agente', 'ver');
  const modCampoAgente = puede('pl_leads_campo_agente', 'modificar');
  const verCampoRef = puede('pl_leads_campo_ref', 'ver');
  const modCampoRef = puede('pl_leads_campo_ref', 'modificar');
  const verCampoOperacion = puede('pl_leads_campo_operacion', 'ver');
  const modCampoOperacion = puede('pl_leads_campo_operacion', 'modificar');
  const verCampoProcedencia = puede('pl_leads_campo_procedencia', 'ver');
  const modCampoProcedencia = puede('pl_leads_campo_procedencia', 'modificar');
  const verCampoTipo = puede('pl_leads_campo_tipo', 'ver');
  const modCampoTipo = puede('pl_leads_campo_tipo', 'modificar');
  const verCampoPoblacion = puede('pl_leads_campo_poblacion', 'ver');
  const modCampoPoblacion = puede('pl_leads_campo_poblacion', 'modificar');
  const verCampoNotas = puede('pl_leads_campo_notas', 'ver');
  const modCampoNotas = puede('pl_leads_campo_notas', 'modificar');
  // Acciones de la ficha del lead (solo "ver").
  const verMoverFrios = puede('pl_leads_ficha_mover_frios', 'ver');
  const verMoverEliminados = puede('pl_leads_ficha_mover_eliminados', 'ver');

  const modoEdicion = !!lead?.id;

  // ── Drag del modal ───────────────────────────────────────────
  const [pos, setPos] = useState(null);
  const dragRef = useRef(null);
  const dragOffsetRef = useRef(null);

  const startDrag = (e) => {
    if (e.target.closest('button')) return;
    const rect = dragRef.current?.getBoundingClientRect();
    if (!rect) return;
    const ox = e.clientX - rect.left;
    const oy = e.clientY - rect.top;
    dragOffsetRef.current = { ox, oy, w: rect.width, h: rect.height };
    document.body.style.userSelect = 'none';

    const onMove = (ev) => {
      if (!dragOffsetRef.current) return;
      const { ox, oy, w, h } = dragOffsetRef.current;
      const x = Math.max(0, Math.min(window.innerWidth  - w, ev.clientX - ox));
      const y = Math.max(0, Math.min(window.innerHeight - h, ev.clientY - oy));
      setPos({ x, y });
    };
    const onUp = () => {
      dragOffsetRef.current = null;
      document.body.style.userSelect = '';
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup',   onUp);
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup',   onUp);
  };

  const [form, setForm] = useState({
    nombre: '', apellido: '', telefono: '', email: '',
    agente_id: '',
    idioma: 'es',
    fuente: '', operacion: '', tipo_interes: '',
    poblacion_interes: '', propiedad_ref: '', notas: '',
  });
  /** Aviso al teclear teléfono/email (como ficha cliente / captación), no solo al guardar. */
  const dupCheckTimerRef = useRef(null);
  const dupCheckSeqRef = useRef(0);
  const formDupRef = useRef(form);
  formDupRef.current = form;

  const [masFuentes, setMasFuentes] = useState(false);
  const [masTipos,   setMasTipos]   = useState(false);

  const [modoPoblacionAvanzado, setModoPoblacionAvanzado] = useState(false);
  const [provinciaSelec, setProvinciaSelec] = useState('');
  const [localidadesBD,  setLocalidadesBD]  = useState([]);
  const [cargandoLocs,   setCargandoLocs]   = useState(false);

  const [fuentes, setFuentes] = useState([]);
  const [tipos,   setTipos]   = useState([]);
  const [agentes, setAgentes] = useState([]);
  /** Evita volver a forzar Ana si el usuario deja "Sin asignar" y se refresca la lista de agentes. */
  const defaultAgenteAplicadoRef = useRef(false);

  const [guardando,      setGuardando]      = useState(false);
  const [error,          setError]          = useState('');
  const [refError,       setRefError]       = useState('');
  const [refValidada,    setRefValidada]    = useState(false);
  const [editandoRef,    setEditandoRef]    = useState(false);
  const [refSugerencias, setRefSugerencias] = useState([]);
  const [refSuggestOpen, setRefSuggestOpen] = useState(false);
  const refInputRef = useRef(null);
  const refSuggestTimerRef = useRef(null);
  /** Solo el botón ✎ debe seleccionar el texto; al teclear el 1.er dígito no (si no, el siguiente lo pisa). */
  const seleccionarRefAlEditarRef = useRef(false);
  const [vistaPreviaPropId, setVistaPreviaPropId] = useState(null);
  const [creado,         setCreado]         = useState(false);
  const [confirmCliente, setConfirmCliente] = useState(null);
  /**
   * Tras Cancelar el aviso de Activos no debe «pasarse» el filtro: se recuerda
   * el teléfono bloqueado hasta que cambie el número o se cree en ese cliente.
   */
  const [activosTelBloqueo, setActivosTelBloqueo] = useState(null);
  /** Teléfono en Fríos: exige ModalCita antes de guardar. */
  const [confirmFriosCita, setConfirmFriosCita] = useState(null);
  const [confirmLeadSuelto, setConfirmLeadSuelto] = useState(null);
  const [confirmPhoneReview, setConfirmPhoneReview] = useState(null);

  const last9FormTel = String(form.telefono || '').replace(/\D/g, '').slice(-9);
  const activosBloqueoVigente = Boolean(
    !modoEdicion &&
      activosTelBloqueo?.last9 &&
      last9FormTel.length >= 9 &&
      last9FormTel === activosTelBloqueo.last9
  );
  const agenteBloqueadoPorClienteActivo = Boolean(
    !modoEdicion &&
      (
        (confirmCliente?.cliente_existente?.en_activos && confirmCliente?.cliente_existente?.agente_id) ||
        (activosBloqueoVigente && activosTelBloqueo?.agenteId)
      )
  );

  useEffect(() => {
    if (!agenteBloqueadoPorClienteActivo) return;
    const agId = String(
      confirmCliente?.cliente_existente?.agente_id || activosTelBloqueo?.agenteId || ''
    );
    if (!agId) return;
    setForm((prev) => (String(prev.agente_id || '') === agId ? prev : { ...prev, agente_id: agId }));
  }, [
    agenteBloqueadoPorClienteActivo,
    confirmCliente?.cliente_existente?.agente_id,
    activosTelBloqueo?.agenteId,
  ]);

  useEffect(() => {
    if (!activosTelBloqueo?.last9) return;
    const cur = String(form.telefono || '').replace(/\D/g, '').slice(-9);
    if (cur.length >= 9 && cur !== activosTelBloqueo.last9) {
      setActivosTelBloqueo(null);
    }
  }, [form.telefono, activosTelBloqueo?.last9]);
  const [msgFocused,     setMsgFocused]     = useState(false);
  const [modalMoverDestino, setModalMoverDestino] = useState(null);
  const [asignandoAgente, setAsignandoAgente] = useState(false);

  // Fuentes (procedencias), tipos y agentes desde API — al abrir el modal para tener datos recientes
  useEffect(() => {
    if (!isOpen) return;
    api.get('/auxiliares/procedencias').then(r => setFuentes(r.data?.data || [])).catch(() => setFuentes([]));
    api.get('/auxiliares/tipo-inmueble').then(r => setTipos(r.data?.data || [])).catch(() => setTipos([]));
    api.get('/agentes').then(r => setAgentes(r.data?.data || [])).catch(() => setAgentes([]));
    api.get('/leads/flujo-catalogo')
      .then((r) => {
        const list = (r.data?.data || [])
          .map((row) => ({ code: row.code, label: row.label || row.code }))
          .filter((row) => row.code);
        if (list.length) setTaskOptions(list);
      })
      .catch(() => setTaskOptions(LEAD_FLOW_TASK_OPTIONS_FALLBACK));
  }, [isOpen]);

  // Nuevo lead: agente por defecto = el del usuario (oficina). Sin Ana automática.
  useEffect(() => {
    if (!isOpen) {
      defaultAgenteAplicadoRef.current = false;
      return;
    }
    if (lead?.id || defaultAgenteAplicadoRef.current) return;
    const propio = usuario?.agente_id ? String(usuario.agente_id) : '';
    setForm((prev) => (prev.agente_id ? prev : { ...prev, agente_id: propio }));
    defaultAgenteAplicadoRef.current = true;
  }, [isOpen, lead?.id, usuario?.agente_id]);

  useEffect(() => {
    if (!isOpen || lead?.id) return;
    setTaskCodeInicial('');
    setErrorTareaInicial(false);
  }, [isOpen, lead?.id]);

  // Cargar localidades cuando cambia provincia
  useEffect(() => {
    if (!provinciaSelec) { setLocalidadesBD([]); return; }
    const prov = PROVINCIAS_ES.find(p => p.nombre === provinciaSelec);
    if (!prov || !PROVINCIAS_CON_BD.includes(prov.id)) { setLocalidadesBD([]); return; }
    setCargandoLocs(true);
    api.get(`/auxiliares/poblaciones/${prov.id}`)
      .then(r => setLocalidadesBD((r.data?.data || []).map(p => p.nombre).sort((a, b) => a.localeCompare(b, 'es'))))
      .catch(() => setLocalidadesBD([]))
      .finally(() => setCargandoLocs(false));
  }, [provinciaSelec]);

  // Rellenar form al abrir en modo edición (también cuando llega el detalle GET
  // con fuente/población que el card de lista aún no traía).
  // Solo al cambiar de lead (o abrir): no cerrar «Mover a Eliminados/Fríos» si
  // un refresco parcial del lead (agente, email…) rellena el formulario.
  useEffect(() => {
    if (!isOpen) return;
    setModalMoverDestino(null);
  }, [isOpen, lead?.id]);

  useEffect(() => {
    if (!isOpen) return;
    if (lead?.id) {
      setEditandoRef(false);
      setForm({
        nombre:            lead.nombre            || '',
        apellido:          lead.apellido          || '',
        telefono:          lead.telefono          || '',
        email:             emailContactoLead(lead) || '',
        agente_id:         agenteIdEnFormulario(lead),
        idioma:            normalizeLanguageCode(lead.idioma) || 'es',
        fuente:            fuenteVisibleEnLead(lead.fuente) || lead.fuente || '',
        operacion:         lead.operacion         || '',
        tipo_interes:      lead.tipo_interes      || '',
        poblacion_interes: (() => {
          const raw = String(lead.poblacion_interes || '').trim();
          if (!raw) return '';
          const canon = POBLACIONES_CHIP.find((p) => mismaPoblacionChip(p, raw));
          return canon || raw;
        })(),
        propiedad_ref:     lead.propiedad_ref     || '',
        notas:             lead.notas             || '',
      });
      setModoPoblacionAvanzado(false);
      setMasFuentes(false);
      setMasTipos(false);
    } else {
      resetForm();
    }
  }, [
    isOpen,
    lead?.id,
    lead?.agente_id,
    lead?.agente_nombre,
    lead?.email,
    lead?.cliente?.email,
    lead?.fuente,
    lead?.poblacion_interes,
    lead?.nombre,
    lead?.apellido,
    lead?.propiedad_ref,
    lead?.tipo_interes,
    lead?.operacion,
  ]);

  // Procedencia seleccionada fuera de las 6 primeras (p. ej. Fotocasa): primera en la fila.
  const fuentesPrincipales = useMemo(() => {
    const base = fuentes.slice(0, MAX_FUENTES_VISIBLES);
    const sel = String(form.fuente || '').trim();
    if (!sel) return base;
    const yaVisible = base.some((f) => mismaProcedencia(f.nombre, sel));
    if (yaVisible) return base;
    const selected = fuentes.find((f) => mismaProcedencia(f.nombre, sel));
    const chip = selected || { id: `sel-${sel}`, nombre: sel };
    return [chip, ...base.filter((f) => String(f.id) !== String(chip.id) && !mismaProcedencia(f.nombre, sel))].slice(
      0,
      MAX_FUENTES_VISIBLES
    );
  }, [fuentes, form.fuente]);
  const fuentesExtra = useMemo(() => {
    const visibles = new Set(fuentesPrincipales.map((f) => String(f.id)));
    const sel = String(form.fuente || '').trim();
    return fuentes.filter(
      (f) => !visibles.has(String(f.id)) && !mismaProcedencia(f.nombre, sel)
    );
  }, [fuentes, fuentesPrincipales, form.fuente]);

  // Población fuera de los chips fijos (p. ej. Oliva): chip primero para que se vea marcada.
  const chipsPoblacion = useMemo(() => {
    const sel = String(form.poblacion_interes || '').trim();
    if (!sel) return POBLACIONES_CHIP;
    if (POBLACIONES_CHIP.some((p) => mismaPoblacionChip(p, sel))) return POBLACIONES_CHIP;
    return [sel, ...POBLACIONES_CHIP];
  }, [form.poblacion_interes]);

  // Si el tipo viene de la ref y está en el «+N», sacarlo a la fila visible.
  const tiposPrincipales = useMemo(() => {
    const base = tipos.slice(0, MAX_TIPOS_VISIBLES);
    const sel = String(form.tipo_interes || '').trim();
    if (!sel) return base;
    if (base.some((t) => String(t.nombre || '').toLowerCase() === sel.toLowerCase())) return base;
    const selected = tipos.find((t) => String(t.nombre || '').toLowerCase() === sel.toLowerCase());
    if (selected) return [selected, ...base];
    // Tipo de la ref no está en catálogo aux → chip sintético visible
    return [{ id: `sel-${sel}`, nombre: sel }, ...base];
  }, [tipos, form.tipo_interes]);
  const tiposExtra = useMemo(() => {
    const visibles = new Set(tiposPrincipales.map((t) => String(t.id)));
    const sel = String(form.tipo_interes || '').trim().toLowerCase();
    return tipos.filter((t) => {
      if (visibles.has(String(t.id))) return false;
      if (sel && String(t.nombre || '').toLowerCase() === sel) return false;
      return true;
    });
  }, [tipos, tiposPrincipales, form.tipo_interes]);

  useEffect(() => {
    const sel = String(form.tipo_interes || '').trim();
    if (!sel || !tipos.length) return;
    const enExtra = tipos.slice(MAX_TIPOS_VISIBLES).some(
      (t) => String(t.nombre || '').toLowerCase() === sel.toLowerCase()
    );
    if (enExtra) setMasTipos(true);
  }, [form.tipo_interes, tipos]);

  useEffect(() => {
    if (!isOpen) return;
    const ref = lead?.propiedad_ref || form.propiedad_ref;
    if (ref?.trim()) validarRef(String(ref).trim());
  }, [isOpen, lead?.id, lead?.propiedad_ref]);

  useEffect(() => {
    if (!editandoRef) return;
    const t = setTimeout(() => {
      refInputRef.current?.focus();
      if (seleccionarRefAlEditarRef.current) {
        refInputRef.current?.select();
        seleccionarRefAlEditarRef.current = false;
      }
    }, 0);
    return () => clearTimeout(t);
  }, [editandoRef]);

  const consultaTextoIdioma = modoEdicion
    ? (lead?.mensaje_original || '')
    : (form.notas || '');
  const effectiveIdioma = useMemo(
    () => resolveEffectiveLeadIdioma({
      idioma: form.idioma,
      mensaje: consultaTextoIdioma,
      mensaje_original: lead?.mensaje_original || null,
    }),
    [form.idioma, consultaTextoIdioma, lead?.mensaje_original],
  );

  // Alta manual: avisar Activos/Fríos/lead suelto al teclear teléfono (debounce), no solo al guardar.
  useEffect(() => {
    if (!isOpen || modoEdicion) return undefined;
    const tel = form.telefono;
    const email = form.email;
    const telOk = telefonoListoParaDup(tel);
    const emOk = emailListoParaDup(email);

    if (dupCheckTimerRef.current) {
      clearTimeout(dupCheckTimerRef.current);
      dupCheckTimerRef.current = null;
    }

    if (!telOk && !emOk) {
      setConfirmCliente(null);
      setConfirmFriosCita(null);
      setConfirmLeadSuelto(null);
      return undefined;
    }

    dupCheckTimerRef.current = setTimeout(async () => {
      const seq = ++dupCheckSeqRef.current;
      try {
        const params = {};
        if (telOk) params.telefono = String(tel).trim();
        if (emOk) params.email = String(email).trim();
        const { data } = await api.get('/clientes/buscar-duplicado', { params, timeout: 15000 });
        if (seq !== dupCheckSeqRef.current) return;
        const snap = formDupRef.current || {};
        const mapped = mapBuscarDuplicadoAConfirm(data, {
          nombre: snap.nombre,
          apellido: snap.apellido,
          telefono: snap.telefono,
          email: snap.email,
        });
        if (!mapped) {
          setConfirmCliente(null);
          setConfirmFriosCita(null);
          setConfirmLeadSuelto(null);
          return;
        }
        setConfirmPhoneReview(null);
        if (mapped.kind === 'frios') {
          setConfirmCliente(null);
          setActivosTelBloqueo(null);
          setConfirmLeadSuelto(null);
          setConfirmFriosCita(mapped.payload);
        } else if (mapped.kind === 'lead_suelto') {
          setConfirmCliente(null);
          setActivosTelBloqueo(null);
          setConfirmFriosCita(null);
          setConfirmLeadSuelto(mapped.payload);
        } else {
          setConfirmFriosCita(null);
          setConfirmLeadSuelto(null);
          setConfirmCliente(mapped.payload);
          if (mapped.payload?.cliente_existente?.en_activos) {
            const last9 = String(snap.telefono || '').replace(/\D/g, '').slice(-9);
            setActivosTelBloqueo({
              last9,
              clienteId: mapped.payload.cliente_existente.id,
              agenteId: mapped.payload.cliente_existente.agente_id || null,
              agenteNombre: mapped.payload.cliente_existente.agente_nombre || null,
              payload: mapped.payload,
            });
          } else {
            setActivosTelBloqueo(null);
          }
        }
      } catch {
        /* silencioso: el 409 al guardar sigue siendo red de seguridad */
      }
    }, 320);

    return () => {
      if (dupCheckTimerRef.current) {
        clearTimeout(dupCheckTimerRef.current);
        dupCheckTimerRef.current = null;
      }
    };
  }, [isOpen, modoEdicion, form.telefono, form.email]);

  if (!isOpen) return null;

  const leadArchivado = modoEdicion && lead && (lead.activo === false || lead.etapa === 'eliminados' || lead.etapa === 'eliminado');
  const puedeMoverTerminal = modoEdicion && lead && lead.activo !== false && !['eliminados', 'eliminado'].includes(lead.etapa);

  const cambiar    = (campo, valor) => setForm(prev => ({ ...prev, [campo]: valor }));
  const toggleChip = (campo, valor) => setForm(prev => ({ ...prev, [campo]: prev[campo] === valor ? '' : valor }));
  const puedeAsignarAgente =
    modoEdicion &&
    !!lead?.id &&
    !leadArchivado &&
    esTitularAnaLead(lead) &&
    !!form.agente_id &&
    !esAgenteAnaSeleccionado(agentes, form.agente_id);

  const aplicarDatosPropiedadAlForm = (prop) => {
    if (!prop) return;
    const tipo = String(prop.tipo_nombre || prop.tipo || '').trim();
    const pob = String(prop.poblacion_nombre || prop.poblacion || '').trim();
    const opRaw = String(prop.operacion || '').trim();
    const op =
      /alquiler/i.test(opRaw) ? 'Alquiler' : /venta/i.test(opRaw) ? 'Venta' : '';
    setForm((prev) => ({
      ...prev,
      tipo_interes: tipo || prev.tipo_interes,
      poblacion_interes: pob || prev.poblacion_interes,
      operacion: op || prev.operacion,
    }));
  };

  const validarRef = async (ref) => {
    if (!ref || ref.trim() === '') { setRefError(''); setRefValidada(false); return false; }
    try {
      const res = await api.get('/propiedades/validar-ref/' + encodeURIComponent(ref.trim()));
      if (res.data.existe) {
        setRefError('');
        setRefValidada(true);
        aplicarDatosPropiedadAlForm(res.data.propiedad);
        return true;
      }
      setRefError('Ref. no existe'); setRefValidada(false); return false;
    } catch { setRefError('Error validando'); setRefValidada(false); return false; }
  };

  const buscarSugerenciasRef = (q) => {
    clearTimeout(refSuggestTimerRef.current);
    const texto = String(q || '').trim();
    if (texto.length < 1) {
      setRefSugerencias([]);
      setRefSuggestOpen(false);
      return;
    }
    refSuggestTimerRef.current = setTimeout(async () => {
      try {
        const r = await api.get(
          `/propiedades?buscar=${encodeURIComponent(texto)}&perPage=8&cartera=todas`
        );
        const rows = r.data?.data || r.data?.propiedades || [];
        setRefSugerencias(Array.isArray(rows) ? rows : []);
        setRefSuggestOpen(true);
      } catch {
        setRefSugerencias([]);
        setRefSuggestOpen(false);
      }
    }, 250);
  };

  const seleccionarSugerenciaRef = async (p) => {
    const ref = String(p?.referencia || '').trim();
    if (!ref) return;
    setForm((prev) => ({ ...prev, propiedad_ref: ref }));
    setRefSugerencias([]);
    setRefSuggestOpen(false);
    setRefError('');
    // validarRef rellena tipo/población/operación (y saca el tipo del +N si hace falta)
    const ok = await validarRef(ref);
    if (ok) setEditandoRef(false);
    else aplicarDatosPropiedadAlForm(p);
  };

  const refMostrarComoEnlace = refValidada && !!form.propiedad_ref?.trim() && !editandoRef;

  const abrirVistaPreviaRef = async (ref) => {
    const referencia = String(ref || form.propiedad_ref || '').trim();
    if (!referencia) return;
    const propId = await resolverPropiedadIdPorReferencia(referencia);
    if (propId) {
      setVistaPreviaPropId(propId);
      return;
    }
    await dialogs.alert(`No se encontró la propiedad ${referencia}`);
  };

  const abrirModalCitaAntesDeAlta = async (pendingOpts = {}) => {
    let propiedades = [];
    const ref = String(form.propiedad_ref || '').trim();
    if (ref) {
      try {
        const prop = await fetchPropiedadPorRef(api, ref);
        if (prop) propiedades = [prop];
      } catch { /* sin ficha */ }
    }
    const agenteSelCita = agentes.find((a) => String(a.id) === String(form.agente_id || ''));
    pendingAltaOptsRef.current = pendingOpts;
    setModalCitaAlta({
      draft: true,
      propiedades,
      agentName: agenteSelCita?.nombre || '',
    });
  };

  const handleGuardar = async (forzarClienteId, opts = {}) => {
    setError('');
    setErrorTareaInicial(false);
    if (refError) { setError('La referencia no es válida'); return; }

    if (!modoEdicion) {
      const telOk = String(form.telefono || '').trim();
      const mailOk = String(form.email || '').trim();
      if (!telOk && !mailOk) {
        setError(MSG_TELEFONO_O_EMAIL);
        return;
      }
      // Activos de otro agente: sin forzar_cliente no se puede «saltar» el aviso con Cancelar.
      const last9Ahora = String(form.telefono || '').replace(/\D/g, '').slice(-9);
      if (
        !forzarClienteId &&
        activosTelBloqueo?.last9 &&
        last9Ahora.length >= 9 &&
        last9Ahora === activosTelBloqueo.last9
      ) {
        if (activosTelBloqueo.payload) setConfirmCliente(activosTelBloqueo.payload);
        setError(
          `Este teléfono está en Activos (${activosTelBloqueo.agenteNombre || 'otro agente'}). ` +
            'Cámbialo o créalo en ese cliente; no puedes asignártelo.'
        );
        return;
      }
      const taskCodeEfectivo = String(
        opts.taskCodeInicial ||
          pendingAltaOptsRef.current?.taskCodeInicial ||
          taskCodeInicial ||
          ''
      ).trim().toUpperCase();
      if (!taskCodeEfectivo) {
        setErrorTareaInicial(true);
        setError(MSG_DEBE_ASIGNAR_TAREA);
        return;
      }
      if (taskCodeEfectivo !== String(taskCodeInicial || '').trim().toUpperCase()) {
        setTaskCodeInicial(taskCodeEfectivo);
      }
      // Agente: no bloquear aquí si aún no hay forzarClienteId — el POST debe
      // poder devolver 409 «teléfono en Activos/Fríos». Con cliente forzado o
      // tras el aviso, sí exigimos agente (bloqueado al del cliente en Activos).
      const agenteOk = String(opts.agenteId || form.agente_id || '').trim();
      if (forzarClienteId && !agenteOk) {
        setError('Debes asignar un agente al lead');
        return;
      }
      // T4·Cita / T3 / T3AE: ModalCita ANTES de crear el lead.
      const agendaPendiente = opts.agenda || pendingAltaOptsRef.current?.agenda || null;
      if (taskRequiereCitaAntesDeAlta(taskCodeEfectivo) && !agendaPendiente) {
        await abrirModalCitaAntesDeAlta({
          forzarClienteId,
          ignorarLeadSuelto: opts.ignorarLeadSuelto,
          ignorarConflictoCliente: opts.ignorarConflictoCliente,
          taskCodeInicial: taskCodeEfectivo,
        });
        return;
      }
      if (agendaPendiente) {
        pendingAltaOptsRef.current = {
          ...pendingAltaOptsRef.current,
          agenda: agendaPendiente,
          forzarClienteId: forzarClienteId ?? pendingAltaOptsRef.current?.forzarClienteId,
          ignorarLeadSuelto: opts.ignorarLeadSuelto ?? pendingAltaOptsRef.current?.ignorarLeadSuelto,
          ignorarConflictoCliente:
            opts.ignorarConflictoCliente ?? pendingAltaOptsRef.current?.ignorarConflictoCliente,
          taskCodeInicial: taskCodeEfectivo,
        };
      }
    }

    setGuardando(true);
    try {
      // Cambio manual de agente en edición: traspasar correctamente (reasigna la
      // tarea y resetea el plazo) en lugar de quedarse solo como "sugerido".
      const agenteSel = form.agente_id || '';
      const agenteActual = lead?.agente_id ? String(lead.agente_id) : '';
      const cambioAgenteReal =
        modoEdicion && !!lead?.id &&
        !!agenteSel && agenteSel !== agenteActual &&
        !esAgenteAnaSeleccionado(agentes, agenteSel);
      if (cambioAgenteReal) {
        const asg = await api.post(`/leads/${lead.id}/asignar-agente`, { agente_id: agenteSel });
        if (!asg?.data?.ok) {
          setError(asg?.data?.error || 'No se pudo asignar el agente');
          setGuardando(false);
          return;
        }
      }

      const payload = {
        ...form,
        fuente: form.fuente || null,
      };
      // Alta en cliente existente: nombre vacío → usar el del cliente (opts o confirm).
      if (!String(payload.nombre || '').trim() && opts.nombreFallback) {
        payload.nombre = String(opts.nombreFallback).trim();
      }
      if (!String(form.telefono || '').trim()) {
        payload.whatsapp = '';
      }
      if (modoEdicion && lead?.mensaje_original) {
        payload.mensaje_original = lead.mensaje_original;
      }
      payload.idioma = form.idioma || 'es';
      // El agente ya se traspasó arriba si cambió a uno real; aquí enviamos el
      // valor efectivo (permite además dejarlo en Ana o sin asignar como admin).
      // opts.agenteId evita carrera con setForm al confirmar Activos.
      payload.agente_id = opts.agenteId || form.agente_id || null;
      if (!modoEdicion) {
        payload.origen_alta = 'manual_crm';
        payload.task_code_inicial = String(
          opts.taskCodeInicial ||
            pendingAltaOptsRef.current?.taskCodeInicial ||
            taskCodeInicial ||
            ''
        ).trim().toUpperCase();
        const agendaBody = opts.agenda || pendingAltaOptsRef.current?.agenda || null;
        if (agendaBody) payload.agenda = agendaBody;
      }
      if (forzarClienteId) payload.forzar_cliente_id = forzarClienteId;
      if (opts.ignorarLeadSuelto) payload.ignorar_lead_suelto = true;
      if (opts.ignorarConflictoCliente) payload.ignorar_conflicto_cliente = true;
      const res = modoEdicion
        ? await api.patch(`/leads/${lead.id}`, payload)
        : await api.post('/leads', payload);
      if (res.data.ok) {
        setConfirmCliente(null);
        setActivosTelBloqueo(null);
        setConfirmFriosCita(null);
        setConfirmLeadSuelto(null);
        setConfirmPhoneReview(null);
        setModalCitaAlta(null);
        pendingAltaOptsRef.current = {};

        setCreado(true);
        setTimeout(() => { setCreado(false); resetForm(); onCreated?.(); onClose?.(); }, 1200);
      }
    } catch (err) {
      const errCode = err.response?.data?.error;
      if (
        err.response?.status === 409 &&
        (errCode === 'conflicto_cliente' || errCode === 'conflicto_cliente_activo')
      ) {
        setConfirmFriosCita(null);
        setConfirmLeadSuelto(null);
        setConfirmPhoneReview(null);
        setConfirmCliente(err.response.data);
        if (err.response.data?.cliente_existente?.en_activos || errCode === 'conflicto_cliente_activo') {
          const last9 = String(form.telefono || '').replace(/\D/g, '').slice(-9);
          setActivosTelBloqueo({
            last9,
            clienteId: err.response.data?.cliente_existente?.id || null,
            agenteId: err.response.data?.cliente_existente?.agente_id || null,
            agenteNombre: err.response.data?.cliente_existente?.agente_nombre || null,
            payload: err.response.data,
          });
        }
        setModalCitaAlta(null);
      } else if (err.response?.status === 409 && errCode === 'cliente_en_frios_requiere_cita') {
        setConfirmCliente(null);
        setActivosTelBloqueo(null);
        setConfirmLeadSuelto(null);
        setConfirmPhoneReview(null);
        setConfirmFriosCita(err.response.data);
        setModalCitaAlta(null);
      } else if (err.response?.status === 409 && errCode === 'conflicto_lead_suelto') {
        setConfirmFriosCita(null);
        setConfirmPhoneReview(null);
        setConfirmLeadSuelto(err.response.data);
        setModalCitaAlta(null);
      } else if (err.response?.status === 409 && errCode === 'revision_telefono_probable') {
        setConfirmCliente(null);
        setConfirmFriosCita(null);
        setConfirmLeadSuelto(null);
        setConfirmPhoneReview(err.response.data);
        setModalCitaAlta(null);
      } else {
        setConfirmPhoneReview(null);
        const msg =
          err.response?.data?.mensaje ||
          err.response?.data?.error ||
          (modoEdicion ? 'Error al guardar cambios' : 'Error al crear el lead');
        if (errCode === 'tarea_requerida' || msg === MSG_DEBE_ASIGNAR_TAREA) {
          setErrorTareaInicial(true);
        }
        if (errCode === 'cita_requerida') {
          setErrorTareaInicial(true);
        }
        setError(msg);
        setModalCitaAlta(null);
      }
    } finally {
      setGuardando(false);
      setGuardandoCitaAlta(false);
    }
  };

  /** Cancelar ModalCita: no se crea lead/tarea; vuelve al formulario. */
  const cerrarModalCitaAltaSinVisita = () => {
    const eraAltaNueva = modalCitaAlta?.draft && !modalCitaAlta?.editAssign;
    setModalCitaAlta(null);
    pendingAltaOptsRef.current = {};
    if (eraAltaNueva) {
      setTaskCodeInicial('');
      setErrorTareaInicial(true);
      setError(MSG_CITA_CANCELADA_ELIGE_TAREA);
    }
  };

  const buildAgendaFromModalCita = (data, { agenteId, nombreContacto }) => {
    const propsVisita = data?.propiedades || [];
    const fechaInicioRaw = buildCitaDateTimeISO(data.fecha, data.horaInicio);
    const bloques = Math.max(1, propsVisita.length);
    const durMin = data.duracion || 45;
    const horaFin = data.horaFin || addMinutosHora(data.horaInicio, bloques * durMin);
    const fechaFinRaw = buildCitaDateTimeISO(data.fecha, horaFin);
    const { fecha_inicio: fechaInicio, fecha_fin: fechaFin } = ensureVisitaRangoISO(
      fechaInicioRaw,
      fechaFinRaw,
      { bloques, duracionMin: durMin },
    );
    if (!fechaInicio || !fechaFin) throw new Error('Fecha u hora de la visita no válidas.');
    const propiedadIds = propsVisita.map((p) => p.id).filter(Boolean);
    const lineasHorario = propsVisita.map((p) => {
      const ref = p.referencia || p.id;
      return `${p.horaVisita || data.horaInicio} · Ref. ${ref}`;
    });
    return {
      agenda: {
        agente_id: String(agenteId),
        usuario_id: usuario?.id ? String(usuario.id) : null,
        propiedad_ids: propiedadIds.length ? propiedadIds : [],
        titulo: `Visita — ${nombreContacto}`,
        descripcion: propsVisita.length
          ? `Visita programada al crear el lead.\n${lineasHorario.join('\n')}`
          : 'Visita programada al crear el lead.',
        fecha_inicio: fechaInicio,
        fecha_fin: fechaFin,
      },
      propsVisita,
    };
  };

  const handleCitaAltaConfirm = async (data) => {
    const ctx = modalCitaAlta;
    if (!ctx?.draft) return;
    try {
      if (data?.cierreMode === 'ana') {
        throw new Error('Ana (IA) aún no está activa. Elige «Yo mismo» para programar la visita.');
      }
      const agenteId =
        (ctx.editAssign ? lead?.agente_id : null) ||
        form.agente_id ||
        usuario?.agente_id ||
        null;
      if (!agenteId) throw new Error('Asigna un agente al lead antes de crear la cita.');

      const nombreContacto = ctx.editAssign
        ? (ctx.contactNombre || 'Cliente')
        : (`${form.nombre || ''} ${form.apellido || ''}`.trim() || 'Cliente');
      const { agenda } = buildAgendaFromModalCita(data, { agenteId, nombreContacto });

      setGuardandoCitaAlta(true);

      // Edición: lead ya existe, solo asignar tarea + cita.
      if (ctx.editAssign && ctx.leadId) {
        const res = await api.post(`/leads/${ctx.leadId}/asignar-tarea-flujo`, {
          task_code: ctx.pendingTaskCode || 'TC',
          agenda,
        });
        if (!res.data?.ok) {
          throw new Error(res.data?.mensaje || res.data?.error || 'No se pudo asignar la tarea');
        }
        setModalCitaAlta(null);
        onLeadUpdated?.(res.data.data);
        onCreated?.();
        setGuardandoCitaAlta(false);
        return;
      }

      const pending = pendingAltaOptsRef.current || {};
      pendingAltaOptsRef.current = { ...pending, agenda };
      await handleGuardar(pending.forzarClienteId, {
        ignorarLeadSuelto: pending.ignorarLeadSuelto,
        ignorarConflictoCliente: pending.ignorarConflictoCliente,
        agenda,
      });
    } catch (e) {
      await dialogs.alert(e?.response?.data?.error || e?.message || 'No se pudo crear la cita');
      setGuardandoCitaAlta(false);
    }
  };

  const asignarLeadAAgente = async () => {
    if (!puedeAsignarAgente) return;
    setError('');
    setAsignandoAgente(true);
    try {
      const res = await api.post(`/leads/${lead.id}/asignar-agente`, {
        agente_id: form.agente_id,
      });
      if (res.data?.ok) {
        setCreado(true);
        setTimeout(() => {
          setCreado(false);
          resetForm();
          onCreated?.();
          onClose?.();
        }, 900);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'No se pudo asignar el lead al agente');
    } finally {
      setAsignandoAgente(false);
    }
  };

  const resetForm = () => {
    setForm({
      nombre: '', apellido: '', telefono: '', email: '',
      agente_id: '',
      idioma: 'es',
      fuente: '', operacion: '', tipo_interes: '', poblacion_interes: '', propiedad_ref: '', notas: '',
    });
    setConfirmCliente(null);
    setActivosTelBloqueo(null);
    setConfirmFriosCita(null);
    setConfirmLeadSuelto(null);
    setConfirmPhoneReview(null);
    setTaskCodeInicial('');
    setErrorTareaInicial(false);
    setRefError(''); setRefValidada(false); setEditandoRef(false); setError('');
    setMasFuentes(false); setMasTipos(false);
    setModalMoverDestino(null);
    setModoPoblacionAvanzado(false); setProvinciaSelec(''); setLocalidadesBD([]);
    setPos(null);
  };

  const cancelarAvisoClienteExistente = () => {
    const enActivos = Boolean(confirmCliente?.cliente_existente?.en_activos);
    if (enActivos) {
      const cli = confirmCliente.cliente_existente;
      const last9 = String(form.telefono || '').replace(/\D/g, '').slice(-9);
      setActivosTelBloqueo({
        last9,
        clienteId: cli?.id || null,
        agenteId: cli?.agente_id || null,
        agenteNombre: cli?.agente_nombre || null,
        payload: confirmCliente,
      });
      setConfirmCliente(null);
      // Vaciar teléfono: no dejar el modal «como si el filtro hubiera pasado».
      setForm((prev) => ({ ...prev, telefono: '' }));
      setError(
        `Teléfono en Activos de ${cli?.agente_nombre || 'otro agente'}. ` +
          'Introduce otro número, o vuelve a escribirlo y créalo en ese cliente.'
      );
      return;
    }
    setConfirmCliente(null);
  };

  const inputStyle = {
    width: '100%', padding: '8px 12px', fontSize: 13,
    border: `1px solid ${c.border}`, borderRadius: 8,
    background: c.bgInput, color: c.textPrimary, outline: 'none',
    fontFamily: 'inherit', transition: 'border-color 0.15s', boxSizing: 'border-box',
  };

  const labelStyle = {
    display: 'block', marginBottom: 6, fontSize: 10, fontWeight: 700,
    textTransform: 'uppercase', letterSpacing: '0.08em', color: c.textMuted,
  };

  const selectStyle = { ...inputStyle, cursor: 'pointer', appearance: 'none' };

  return (
    <div
      onClick={() => {
        // Con ModalCita abierto no cerrar el alta al clicar el fondo.
        if (modalCitaAlta) return;
        onClose?.();
        resetForm();
      }}
      style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 1000, fontFamily: "'DM Sans', sans-serif",
    }}>
      <div ref={dragRef} onClick={e => e.stopPropagation()} style={{
        width: 1520, maxWidth: '97vw', maxHeight: '94vh',
        display: 'flex', flexDirection: 'column',
        background: isDark ? c.bgCard : '#c8c8be', borderRadius: 16, overflow: 'hidden',
        border: `1px solid ${isDark ? c.border : '#a8a8a0'}`,
        boxShadow: isDark
          ? '0 0 60px 15px rgba(255,255,255,0.18), 0 0 120px 40px rgba(255,255,255,0.10)'
          : '0 16px 48px rgba(0,0,0,0.30), 0 4px 12px rgba(0,0,0,0.15)',
        position: pos ? 'fixed' : 'relative',
        ...(pos ? { left: pos.x, top: pos.y, margin: 0 } : {}),
        // Mientras se agenda la cita, el formulario queda detrás del ModalCita.
        visibility: modalCitaAlta ? 'hidden' : 'visible',
        pointerEvents: modalCitaAlta ? 'none' : 'auto',
      }}>

        {/* Overlay confirmación */}
        {creado && (
          <div style={{ position: 'absolute', inset: 0, borderRadius: 14, background: c.bgCard, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12, zIndex: 2 }}>
            <div style={{ fontSize: 48 }}>{modoEdicion ? '✏️' : '✅'}</div>
            <div style={{ fontSize: 16, fontWeight: 700, color: ml.blue }}>{modoEdicion ? 'Cambios guardados' : 'Lead creado'}</div>
            <div style={{ fontSize: 13, color: c.textMuted, fontWeight: 500 }}>{form.nombre} {form.apellido} {modoEdicion ? 'actualizado' : 'añadido al pipeline'}</div>
          </div>
        )}

        {/* Chrome a ancho completo: barra azul + cabecera (incluye panel derecho) */}
        <div style={{ height: 3, flexShrink: 0, background: `linear-gradient(90deg, ${ml.blueLight}, ${ml.blue}, ${ml.blueFrom}, ${ml.blue}, ${ml.blueLight})` }} />

        <div onMouseDown={startDrag} style={{ display: 'flex', alignItems: 'stretch', flexShrink: 0, cursor: 'grab', userSelect: 'none', background: isDark ? 'transparent' : '#e0e0d8', borderBottom: `1px solid ${isDark ? c.border : '#a8a8a0'}` }}>
          <div style={{ flex: 1, minWidth: 0, padding: '14px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
              <div style={{ width: 38, height: 38, borderRadius: 10, background: `linear-gradient(135deg, ${ml.blueFrom}, ${ml.blueTo})`, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: `0 4px 12px ${ml.blueShadow}`, flexShrink: 0 }}>
                <svg width={17} height={17} viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round">
                  {modoEdicion
                    ? <><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/></>
                    : <><path d="M16 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="8.5" cy="7" r="4"/><line x1="20" y1="8" x2="20" y2="14"/><line x1="23" y1="11" x2="17" y2="11"/></>
                  }
                </svg>
              </div>
              <div style={{ minWidth: 0 }}>
                <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: c.textPrimary }}>{modoEdicion ? 'Editar lead' : 'Nuevo lead'}</h2>
                <p style={{ margin: 0, marginTop: 1, fontSize: 11, fontWeight: 500, color: c.textMuted }}>{modoEdicion ? `${lead.nombre || ''} ${lead.apellido || ''}`.trim() : 'Creación manual'}</p>
              </div>
            </div>
            <button type="button" onClick={() => { onClose?.(); resetForm(); setModalMoverDestino(null); }} style={{ width: 30, height: 30, borderRadius: 8, border: `1px solid ${c.border}`, background: c.bgInput, cursor: 'pointer', fontSize: 15, color: c.textMuted, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>✕</button>
          </div>
          {!modoEdicion && (
            <div style={{ width: 320, flexShrink: 0, padding: '14px 16px', borderLeft: `1px solid ${isDark ? c.border : '#a8a8a0'}`, boxSizing: 'border-box', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: c.textPrimary }}>Alta manual</div>
              <div style={{ fontSize: 11, color: c.textMuted, marginTop: 2, lineHeight: 1.35 }}>
                Oficina / atención directa: elige agente y la tarea desde la que sigue el embudo.
              </div>
            </div>
          )}
        </div>

        {/* Cuerpo: formulario + panel derecho */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'row', overflow: 'hidden', minHeight: 0 }}>

        {/* ══════ COLUMNA IZQUIERDA ══════ */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minWidth: 0 }}>

          {puedeMoverTerminal && (verMoverFrios || verMoverEliminados) && (
            <div style={{ padding: '10px 22px 0', flexShrink: 0 }}>
              <div style={{ ...labelStyle, marginBottom: 8 }}>Archivo manual</div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {verMoverFrios && (
                <button
                  type="button"
                  onClick={() => setModalMoverDestino('frios')}
                  style={{
                    padding: '8px 14px', borderRadius: 10, border: '1px solid rgba(100,116,139,0.5)', fontWeight: 700, fontSize: 12,
                    background: 'linear-gradient(135deg,rgba(100,116,139,0.2),rgba(59,130,246,0.15))', color: c.textPrimary, cursor: 'pointer', fontFamily: 'inherit',
                  }}
                >
                  ❄️ Mover a Fríos
                </button>
                )}
                {verMoverEliminados && (
                <button
                  type="button"
                  onClick={() => setModalMoverDestino('eliminados')}
                  style={{
                    padding: '8px 14px', borderRadius: 10, border: '1px solid rgba(239,68,68,0.45)', fontWeight: 700, fontSize: 12,
                    background: 'linear-gradient(135deg,rgba(239,68,68,0.15),rgba(185,28,28,0.12))', color: c.textPrimary, cursor: 'pointer', fontFamily: 'inherit',
                  }}
                >
                  🗑️ Mover a Eliminados
                </button>
                )}
              </div>
            </div>
          )}

          {leadArchivado && (
            <div style={{ padding: '10px 22px 0', flexShrink: 0 }}>
              <div style={{ padding: '10px 12px', borderRadius: 10, fontSize: 12, fontWeight: 600, lineHeight: 1.45, background: isDark ? 'rgba(239,68,68,0.12)' : '#fef2f2', color: isDark ? '#fca5a5' : '#991b1b', border: `1px solid ${isDark ? 'rgba(239,68,68,0.35)' : '#fecaca'}` }}>
                Este lead está en Eliminados: el formulario es solo consulta. Restaura el lead desde la papelera del pipeline para editarlo o reactivar automatizaciones.
              </div>
            </div>
          )}

          {/* Cuerpo scrollable */}
          <div style={{
            flex: 1, overflowY: 'auto', overflowX: 'hidden',
            opacity: leadArchivado ? 0.58 : 1,
            pointerEvents: leadArchivado ? 'none' : 'auto',
          }}>

            {/* Datos de contacto */}
            <div style={{ padding: '14px 22px 0' }}>
              <div style={labelStyle}>Datos de contacto</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                {verNombre && (
                <input value={form.nombre}   onChange={e => cambiar('nombre', e.target.value)}   placeholder="Nombre"   disabled={!modNombre} title={!modNombre ? tituloSinPermiso : undefined} style={{ ...inputStyle, opacity: modNombre ? 1 : 0.6, cursor: modNombre ? 'text' : 'not-allowed' }} onFocus={e => e.target.style.borderColor = ml.blue} onBlur={e => e.target.style.borderColor = c.border} />
                )}
                {verNombre && (
                <input value={form.apellido} onChange={e => cambiar('apellido', e.target.value)} placeholder="Apellido" disabled={!modNombre} title={!modNombre ? tituloSinPermiso : undefined} style={{ ...inputStyle, opacity: modNombre ? 1 : 0.6, cursor: modNombre ? 'text' : 'not-allowed' }} onFocus={e => e.target.style.borderColor = ml.blue} onBlur={e => e.target.style.borderColor = c.border} />
                )}
                {verTelefono && (
                <input value={form.telefono} onChange={e => cambiar('telefono', e.target.value)} placeholder="Teléfono" disabled={!modTelefono} title={!modTelefono ? tituloSinPermiso : undefined} style={{ ...inputStyle, opacity: modTelefono ? 1 : 0.6, cursor: modTelefono ? 'text' : 'not-allowed' }} onFocus={e => e.target.style.borderColor = ml.blue} onBlur={e => e.target.style.borderColor = c.border} />
                )}
                {verEmail && (
                <input value={form.email}    onChange={e => cambiar('email', e.target.value)}    placeholder="Email" type="email" disabled={!modEmail} title={!modEmail ? tituloSinPermiso : undefined} style={{ ...inputStyle, opacity: modEmail ? 1 : 0.6, cursor: modEmail ? 'text' : 'not-allowed' }} onFocus={e => e.target.style.borderColor = ml.blue} onBlur={e => e.target.style.borderColor = c.border} />
                )}
              </div>
            </div>

            {/* Idioma */}
            {verIdioma && (
            <div style={{ padding: '12px 22px 0' }}>
              <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
                <div style={labelStyle}>Idioma</div>
                <span style={{ fontSize: 10, fontWeight: 700, color: c.textMuted }}>
                  WhatsApp: {formatIdiomaBadge(effectiveIdioma.code)}
                </span>
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, opacity: modIdioma ? 1 : 0.6 }} title={!modIdioma ? tituloSinPermiso : undefined}>
                {LEAD_IDIOMA_OPTIONS.map((opt) => (
                  <ChipBtn
                    key={opt.code}
                    compact
                    activo={form.idioma === opt.code}
                    onClick={() => { if (modIdioma) cambiar('idioma', opt.code); }}
                  >
                    {opt.label}
                  </ChipBtn>
                ))}
              </div>
              {lead?.mensaje_original && (
                <div style={{ marginTop: 8, fontSize: 11, color: c.textMuted, lineHeight: 1.45 }}>
                  <span style={{ fontWeight: 600, color: c.textSecondary }}>Consulta del portal: </span>
                  {String(lead.mensaje_original).slice(0, 160)}
                  {lead.mensaje_original.length > 160 ? '…' : ''}
                </div>
              )}
              {effectiveIdioma.overridesField && (
                <div style={{
                  marginTop: 8, padding: '8px 10px', borderRadius: 8, fontSize: 11, fontWeight: 600, lineHeight: 1.45,
                  background: isDark ? 'rgba(245,158,11,0.12)' : '#fffbeb',
                  color: isDark ? '#fcd34d' : '#92400e',
                  border: `1px solid ${isDark ? 'rgba(245,158,11,0.35)' : '#fde68a'}`,
                }}>
                  El texto de la consulta indica {formatIdiomaBadge(effectiveIdioma.code)}; prevalece sobre el idioma del portal (
                  {formatIdiomaBadge(effectiveIdioma.fieldCode)}).
                </div>
              )}
            </div>
            )}

            {/* Agente + Preagente + Referencia + Operación — una fila */}
            <div style={{ padding: '10px 22px 0', minWidth: 0 }}>
              <div style={{
                display: 'grid',
                gridTemplateColumns: verCampoAgente
                  ? 'minmax(0, 1fr) minmax(0, 1.15fr) minmax(0, 0.75fr) auto'
                  : 'minmax(0, 0.85fr) auto',
                gap: 10,
                alignItems: 'start',
              }}>
                {verCampoAgente && (
                <div style={{ minWidth: 0 }}>
                  <div style={labelStyle}>Agente</div>
                  <div style={{ display: 'flex', gap: 6, alignItems: 'stretch' }}>
                    <select
                      value={form.agente_id || ''}
                      onChange={(e) => cambiar('agente_id', e.target.value)}
                      disabled={!modCampoAgente || agenteBloqueadoPorClienteActivo}
                      title={
                        agenteBloqueadoPorClienteActivo
                          ? 'Cliente en Activos: el lead queda con su agente'
                          : (!modCampoAgente ? tituloSinPermiso : undefined)
                      }
                      style={{
                        ...selectStyle,
                        flex: 1,
                        minWidth: 0,
                        opacity: !modCampoAgente || agenteBloqueadoPorClienteActivo ? 0.6 : 1,
                        cursor: !modCampoAgente || agenteBloqueadoPorClienteActivo ? 'not-allowed' : 'pointer',
                      }}
                    >
                      <option value="">Sin asignar</option>
                      {agentes.map((a) => (
                        <option key={a.id} value={a.id}>{a.nombre}</option>
                      ))}
                    </select>
                    {puedeAsignarAgente && modCampoAgente && (
                      <button
                        type="button"
                        onClick={asignarLeadAAgente}
                        disabled={asignandoAgente}
                        style={{
                          padding: '0 10px',
                          borderRadius: 8,
                          border: '1px solid rgba(250,204,21,0.45)',
                          background: 'linear-gradient(135deg, rgba(250,204,21,0.24), rgba(234,179,8,0.16))',
                          color: '#ffffff',
                          fontSize: 11,
                          fontWeight: 700,
                          cursor: asignandoAgente ? 'not-allowed' : 'pointer',
                          fontFamily: 'inherit',
                          opacity: asignandoAgente ? 0.7 : 1,
                          flexShrink: 0,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {asignandoAgente ? '…' : 'Asignar'}
                      </button>
                    )}
                  </div>
                </div>
                )}
                {verCampoAgente && (() => {
                  const pre = preagenteAsignadoInfo(lead, agentes);
                  // Motivo solo en Pruebas; en la ficha basta el nombre del comercial.
                  const texto = pre.nombre || 'Sin preasignar';
                  return (
                    <div style={{ minWidth: 0 }}>
                      <div style={labelStyle}>Preagente asignado</div>
                      <div
                        title={texto}
                        style={{
                          ...selectStyle,
                          display: 'flex',
                          alignItems: 'center',
                          minHeight: 37,
                          opacity: 0.95,
                          cursor: 'default',
                          color: pre.nombre ? (isDark ? '#e2e8f0' : '#0f172a') : (isDark ? '#94a3b8' : '#64748b'),
                          fontSize: 13,
                          lineHeight: 1.25,
                        }}
                      >
                        {texto}
                      </div>
                    </div>
                  );
                })()}
                {verCampoRef && (
                <div style={{ minWidth: 0 }}>
                  <div style={labelStyle}>Referencia por la que ha entrado</div>
                  {refMostrarComoEnlace ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, minHeight: 37 }}>
                      <CitaLinkReferencia
                        referencia={`Ref. ${form.propiedad_ref}`}
                        onAbrir={() => abrirVistaPreviaRef(form.propiedad_ref)}
                        color={isDark ? '#60a5fa' : c.accentBlue}
                        style={{
                          fontSize: 13,
                          fontWeight: 700,
                          padding: '6px 10px',
                          borderRadius: 8,
                          background: c.accentBlueLight,
                          border: `1px solid ${c.accentBlueBorder}`,
                          textDecoration: 'underline',
                        }}
                      />
                      {modCampoRef && (
                      <button
                        type="button"
                        title="Editar referencia"
                        onClick={() => {
                          seleccionarRefAlEditarRef.current = true;
                          setEditandoRef(true);
                        }}
                        style={{
                          width: 30,
                          height: 30,
                          flexShrink: 0,
                          borderRadius: 8,
                          border: `1px solid ${c.border}`,
                          background: c.bgInput,
                          color: c.textMuted,
                          cursor: 'pointer',
                          fontSize: 14,
                          lineHeight: 1,
                          fontFamily: 'inherit',
                        }}
                      >
                        ✎
                      </button>
                      )}
                    </div>
                  ) : (
                    <div style={{ position: 'relative' }}>
                      <input
                        ref={refInputRef}
                        value={form.propiedad_ref}
                        disabled={!modCampoRef}
                        title={!modCampoRef ? tituloSinPermiso : undefined}
                        onChange={e => {
                          const v = e.target.value;
                          cambiar('propiedad_ref', v);
                          setRefError('');
                          setRefValidada(false);
                          setEditandoRef(true);
                          if (modCampoRef) buscarSugerenciasRef(v);
                        }}
                        onFocus={() => {
                          if (modCampoRef && form.propiedad_ref?.trim()) buscarSugerenciasRef(form.propiedad_ref);
                        }}
                        onBlur={async (e) => {
                          setTimeout(() => setRefSuggestOpen(false), 150);
                          const ok = await validarRef(e.target.value);
                          if (ok) setEditandoRef(false);
                        }}
                        placeholder="Ej: 3428-C"
                        autoComplete="off"
                        style={{
                          ...inputStyle,
                          borderColor: refError ? '#ef4444' : refValidada ? '#22c55e' : c.border,
                          paddingRight: refError ? 28 : undefined,
                          opacity: modCampoRef ? 1 : 0.6,
                          cursor: modCampoRef ? 'text' : 'not-allowed',
                        }}
                      />
                      {refError && <span style={{ position: 'absolute', right: 9, top: '50%', transform: 'translateY(-50%)', fontSize: 13, color: '#ef4444' }}>✗</span>}
                      {refSuggestOpen && refSugerencias.length > 0 && (
                        <div
                          style={{
                            position: 'absolute',
                            top: '100%',
                            left: 0,
                            right: 0,
                            zIndex: 80,
                            marginTop: 2,
                            maxHeight: 180,
                            overflowY: 'auto',
                            borderRadius: 8,
                            border: `1px solid ${c.border}`,
                            background: isDark ? '#1e2330' : '#fff',
                            boxShadow: '0 8px 20px rgba(0,0,0,0.18)',
                          }}
                        >
                          {refSugerencias.map((p) => (
                            <div
                              key={p.id || p.referencia}
                              onMouseDown={(e) => {
                                e.preventDefault();
                                seleccionarSugerenciaRef(p);
                              }}
                              style={{
                                padding: '7px 10px',
                                cursor: 'pointer',
                                fontSize: 12,
                                borderBottom: `1px solid ${isDark ? '#2d3548' : '#f1f5f9'}`,
                                color: c.textPrimary,
                              }}
                            >
                              <strong>{p.referencia}</strong>
                              {(p.poblacion_nombre || p.poblacion || p.direccion) ? (
                                <span style={{ color: c.textMuted }}>
                                  {' '}— {p.poblacion_nombre || p.poblacion || p.direccion}
                                </span>
                              ) : null}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                  {refError && <div style={{ fontSize: 10, color: '#ef4444', fontWeight: 600, marginTop: 3 }}>{refError}</div>}
                </div>
                )}
                {verCampoOperacion && (
                <div>
                  <div style={labelStyle}>Operación</div>
                  <div style={{ display: 'flex', gap: 5, flexWrap: 'nowrap', opacity: modCampoOperacion ? 1 : 0.6 }} title={!modCampoOperacion ? tituloSinPermiso : undefined}>
                    {OPERACIONES.map(op => (
                      <ChipBtn key={op.id} compact activo={form.operacion === op.id} onClick={() => { if (modCampoOperacion) toggleChip('operacion', op.id); }} colorActivo={op.id === 'Alquiler' ? '#ec4899' : '#16a34a'}>
                        {op.emoji} {op.label}
                      </ChipBtn>
                    ))}
                  </div>
                </div>
                )}
              </div>
            </div>

            {/* Procedencia — chips + “más” en un solo flex (el botón queda al final) */}
            {verCampoProcedencia && (
            <div style={{ padding: '12px 22px 0', opacity: modCampoProcedencia ? 1 : 0.6 }} title={!modCampoProcedencia ? tituloSinPermiso : undefined}>
              <div style={labelStyle}>Procedencia</div>
              {fuentes.length === 0 && (
                <p style={{ fontSize: 11, color: c.textMuted, margin: '0 0 8px', lineHeight: 1.45 }}>
                  No hay procedencias en la base de datos. Créalas en{' '}
                  <strong style={{ color: c.textSecondary }}>Configuración → Tablas auxiliares → Procedencias</strong>.
                </p>
              )}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
                {fuentesPrincipales.map(f => (
                  <ChipBtn
                    key={f.id}
                    activo={mismaProcedencia(form.fuente, f.nombre)}
                    onClick={() => {
                      if (!modCampoProcedencia) return;
                      cambiar('fuente', mismaProcedencia(form.fuente, f.nombre) ? '' : f.nombre);
                    }}
                  >
                    {emojiVisualProcedencia(f.nombre, f.emoji)} {f.nombre}
                  </ChipBtn>
                ))}
                {masFuentes && fuentesExtra.map(f => (
                  <ChipBtn
                    key={f.id}
                    activo={mismaProcedencia(form.fuente, f.nombre)}
                    onClick={() => {
                      if (!modCampoProcedencia) return;
                      cambiar('fuente', mismaProcedencia(form.fuente, f.nombre) ? '' : f.nombre);
                    }}
                  >
                    {emojiVisualProcedencia(f.nombre, f.emoji)} {f.nombre}
                  </ChipBtn>
                ))}
                {fuentesExtra.length > 0 && (
                  <MasBtn activo={masFuentes} onClick={() => setMasFuentes(!masFuentes)}>
                    {masFuentes ? '▾ Menos' : `▸ +${fuentesExtra.length}`}
                  </MasBtn>
                )}
              </div>
            </div>
            )}


            {/* Tipo de inmueble */}
            {verCampoTipo && (
            <div style={{ padding: '12px 22px 0', opacity: modCampoTipo ? 1 : 0.6 }} title={!modCampoTipo ? tituloSinPermiso : undefined}>
              <div style={labelStyle}>Tipo de inmueble</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {tiposPrincipales.map(t => (
                  <ChipBtn key={t.id} activo={form.tipo_interes === t.nombre} onClick={() => { if (modCampoTipo) toggleChip('tipo_interes', t.nombre); }}>
                    {t.nombre}
                  </ChipBtn>
                ))}
                {tiposExtra.length > 0 && (
                  <MasBtn activo={masTipos} onClick={() => setMasTipos(!masTipos)}>
                    {masTipos ? '▾ Menos' : `▸ +${tiposExtra.length}`}
                  </MasBtn>
                )}
              </div>
              {masTipos && tiposExtra.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 6 }}>
                  {tiposExtra.map(t => (
                    <ChipBtn key={t.id} activo={form.tipo_interes === t.nombre} onClick={() => { if (modCampoTipo) toggleChip('tipo_interes', t.nombre); }}>
                      {t.nombre}
                    </ChipBtn>
                  ))}
                </div>
              )}
            </div>
            )}

            {/* Población */}
            {verCampoPoblacion && (
            <div style={{ padding: '12px 22px 0', opacity: modCampoPoblacion ? 1 : 0.6, pointerEvents: modCampoPoblacion ? 'auto' : 'none' }} title={!modCampoPoblacion ? tituloSinPermiso : undefined}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                <div style={labelStyle}>Población</div>
                {form.poblacion_interes && <span style={{ fontSize: 10, color: ml.chipText, fontWeight: 600 }}>✓ {form.poblacion_interes}</span>}
              </div>

              {!modoPoblacionAvanzado ? (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {chipsPoblacion.map(p => (
                    <ChipBtn
                      key={p}
                      activo={mismaPoblacionChip(form.poblacion_interes, p)}
                      onClick={() => {
                        cambiar(
                          'poblacion_interes',
                          mismaPoblacionChip(form.poblacion_interes, p) ? '' : p
                        );
                        setModoPoblacionAvanzado(false);
                      }}
                    >
                      {p}
                    </ChipBtn>
                  ))}
                  <MasBtn activo={false} onClick={() => setModoPoblacionAvanzado(true)}>▸ Más</MasBtn>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <div>
                    <button onClick={() => { setModoPoblacionAvanzado(false); setProvinciaSelec(''); setLocalidadesBD([]); }} style={{ padding: '5px 10px', borderRadius: 6, fontSize: 11, fontWeight: 600, border: `1px solid ${c.border}`, background: c.bgInput, color: c.textMuted, cursor: 'pointer', fontFamily: 'inherit' }}>
                      ← Volver
                    </button>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                    <div>
                      <div style={{ fontSize: 10, fontWeight: 700, color: c.textMuted, marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Provincia</div>
                      <select value={provinciaSelec} onChange={e => { setProvinciaSelec(e.target.value); cambiar('poblacion_interes', ''); }} style={selectStyle}>
                        <option value="">Selecciona provincia…</option>
                        {PROVINCIAS_ES.map(p => <option key={p.id} value={p.nombre}>{p.nombre}</option>)}
                      </select>
                    </div>
                    <div>
                      <div style={{ fontSize: 10, fontWeight: 700, color: c.textMuted, marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Localidad</div>
                      {cargandoLocs ? (
                        <div style={{ fontSize: 11, color: c.textMuted, padding: '8px 0' }}>Cargando…</div>
                      ) : localidadesBD.length > 0 ? (
                        <select value={form.poblacion_interes} onChange={e => cambiar('poblacion_interes', e.target.value)} style={selectStyle} disabled={!provinciaSelec}>
                          <option value="">Selecciona localidad…</option>
                          {localidadesBD.map(l => <option key={l} value={l}>{l}</option>)}
                        </select>
                      ) : (
                        <input value={form.poblacion_interes} onChange={e => cambiar('poblacion_interes', e.target.value)}
                          placeholder={provinciaSelec === 'Extranjero' ? 'Ciudad / País…' : 'Escribe la localidad…'}
                          style={inputStyle} disabled={!provinciaSelec} />
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
            )}

            {/* Nota rápida */}
            {verCampoNotas && (
            <div style={{ padding: '12px 22px 0' }}>
              <div style={labelStyle}>Nota rápida</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
                {NOTAS_RAPIDAS.map(n => (
                  <button key={n.id} disabled={!modCampoNotas} onClick={() => { if (modCampoNotas) cambiar('notas', form.notas ? form.notas + '\n' + n.text : n.text); }} style={{ padding: '5px 10px', borderRadius: 6, fontSize: 10, fontWeight: 600, border: `1px solid ${ml.blueBorder}`, background: ml.blueBg, color: ml.chipText, cursor: modCampoNotas ? 'pointer' : 'not-allowed', fontFamily: 'inherit', opacity: modCampoNotas ? 1 : 0.6 }}>
                    {n.emoji} {n.label}
                  </button>
                ))}
              </div>
              <textarea value={form.notas} onChange={e => cambiar('notas', e.target.value)} placeholder="Notas adicionales..." rows={3}
                disabled={!modCampoNotas} title={!modCampoNotas ? tituloSinPermiso : undefined}
                style={{ ...inputStyle, resize: 'vertical', minHeight: 56, maxHeight: 130, lineHeight: 1.5, borderColor: msgFocused ? ml.blue : c.border, opacity: modCampoNotas ? 1 : 0.6, cursor: modCampoNotas ? 'text' : 'not-allowed' }}
                onFocus={() => setMsgFocused(true)} onBlur={() => setMsgFocused(false)} />
            </div>
            )}

            {/* Confirmación cliente existente */}
            {confirmCliente && (
              <div ref={el => { if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' }); }} style={{ padding: '12px 22px 0' }}>
                <div style={{ padding: 14, borderRadius: 10, fontSize: 12, lineHeight: 1.6, background: isDark ? '#2a2010' : '#fffbeb', border: `1px solid ${isDark ? '#5c4a1d' : '#fde68a'}`, color: isDark ? '#fcd34d' : '#92400e' }}>
                  <p style={{ fontWeight: 700, marginBottom: 8, fontSize: 13 }}>
                    ⚠️ {confirmCliente.cliente_existente?.en_activos
                      ? `Teléfono en Activos — agente ${confirmCliente.cliente_existente?.agente_nombre || confirmCliente.agente_nombre || '—'}`
                      : (confirmCliente.duplicate_type === 'exact'
                        ? 'Este teléfono o email ya existe en otro cliente.'
                        : 'Es muy posible que este teléfono o email sea de este otro cliente. Compruébalo bien antes de seguir.')}
                  </p>
                  <p style={{ marginTop: 0, marginBottom: 10, opacity: 0.92 }}>
                    {confirmCliente.mensaje}
                    {!confirmCliente.mensaje && confirmCliente.reason ? ` Motivo: ${confirmCliente.reason}.` : ''}
                    {!confirmCliente.cliente_existente?.en_activos && confirmCliente.agente_nombre
                      ? ` Agente actual: ${confirmCliente.agente_nombre}.`
                      : ''}
                  </p>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 12 }}>
                    <div>
                      <div style={{ fontSize: 10, fontWeight: 600, opacity: 0.7, marginBottom: 3 }}>Cliente existente:</div>
                      <div style={{ fontWeight: 600 }}>{confirmCliente.cliente_existente.nombre}</div>
                      <div>{confirmCliente.cliente_existente.email || '—'}</div>
                      <div>{confirmCliente.cliente_existente.telefono || '—'}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: 10, fontWeight: 600, opacity: 0.7, marginBottom: 3 }}>Datos nuevos:</div>
                      <div style={{ fontWeight: 600 }}>{confirmCliente.datos_nuevos.nombre}</div>
                      <div>{confirmCliente.datos_nuevos.email || '—'}</div>
                      <div>{confirmCliente.datos_nuevos.telefono || '—'}</div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <button
                      type="button"
                      disabled={guardando}
                      onClick={() => {
                        const cli = confirmCliente.cliente_existente;
                        const agId = cli?.agente_id ? String(cli.agente_id) : '';
                        const nombreCli = String(cli?.nombre || '').trim();
                        if (agId || nombreCli) {
                          setForm((prev) => ({
                            ...prev,
                            ...(agId ? { agente_id: agId } : {}),
                            ...(!String(prev.nombre || '').trim() && nombreCli
                              ? { nombre: nombreCli }
                              : {}),
                          }));
                        }
                        handleGuardar(cli.id, {
                          agenda: pendingAltaOptsRef.current?.agenda,
                          agenteId: agId || undefined,
                          nombreFallback: nombreCli || undefined,
                        });
                      }}
                      style={{ flex: 1, padding: '8px 12px', borderRadius: 6, fontSize: 11, fontWeight: 600, background: ml.blue, color: '#fff', border: 'none', cursor: guardando ? 'wait' : 'pointer', fontFamily: 'inherit', opacity: guardando ? 0.7 : 1 }}
                    >
                      {guardando
                        ? 'Creando…'
                        : confirmCliente.cliente_existente?.en_activos
                        ? `Crear lead en el cliente de ${confirmCliente.cliente_existente?.agente_nombre || 'ese agente'}`
                        : `Sí, asignar a ${confirmCliente.cliente_existente.nombre}`}
                    </button>
                    {confirmCliente.permite_crear_aparte !== false &&
                      (confirmCliente.duplicate_type !== 'exact' || puedeCrearIgualmenteExacto) && (
                      <button
                        onClick={() => handleGuardar(undefined, {
                          ignorarConflictoCliente: true,
                          agenda: pendingAltaOptsRef.current?.agenda,
                        })}
                        style={{ flex: 1, minWidth: 160, padding: '8px 12px', borderRadius: 6, fontSize: 11, fontWeight: 600, background: 'transparent', color: c.textPrimary, border: `1px solid ${c.border}`, cursor: 'pointer', fontFamily: 'inherit' }}
                      >
                        Crear igualmente
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={cancelarAvisoClienteExistente}
                      style={{ padding: '8px 12px', borderRadius: 6, fontSize: 11, fontWeight: 500, background: 'transparent', color: c.textMuted, border: `1px solid ${c.border}`, cursor: 'pointer', fontFamily: 'inherit' }}
                    >
                      Cancelar
                    </button>
                  </div>
                </div>
              </div>
            )}

            {!confirmCliente && activosBloqueoVigente && (
              <div style={{ padding: '12px 22px 0' }}>
                <div style={{
                  padding: '10px 12px', borderRadius: 8, fontSize: 12, lineHeight: 1.45,
                  background: isDark ? '#2a2010' : '#fffbeb',
                  border: `1px solid ${isDark ? '#5c4a1d' : '#fde68a'}`,
                  color: isDark ? '#fcd34d' : '#92400e',
                }}>
                  Teléfono en Activos de <strong>{activosTelBloqueo.agenteNombre || 'otro agente'}</strong>.
                  Cámbialo o pulsa «Crear lead en ese cliente» tras volver a introducirlo.
                  <button
                    type="button"
                    onClick={() => {
                      if (activosTelBloqueo.payload) setConfirmCliente(activosTelBloqueo.payload);
                    }}
                    style={{
                      display: 'block', marginTop: 8, padding: '6px 10px', borderRadius: 6,
                      border: 'none', background: ml.blue, color: '#fff', fontSize: 11, fontWeight: 600,
                      cursor: 'pointer', fontFamily: 'inherit',
                    }}
                  >
                    Ver aviso de nuevo
                  </button>
                </div>
              </div>
            )}

            {confirmFriosCita && (
              <div ref={el => { if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' }); }} style={{ padding: '12px 22px 0' }}>
                <div style={{ padding: 14, borderRadius: 10, fontSize: 12, lineHeight: 1.6, background: isDark ? '#1a2332' : '#eff6ff', border: `1px solid ${isDark ? '#1e3a5f' : '#bfdbfe'}`, color: isDark ? '#93c5fd' : '#1e40af' }}>
                  <p style={{ fontWeight: 700, marginBottom: 8, fontSize: 13 }}>
                    ❄️ Teléfono en Fríos — agente {confirmFriosCita.cliente_existente?.agente_nombre || confirmFriosCita.agente_nombre || '—'}
                  </p>
                  <p style={{ marginTop: 0, marginBottom: 12, opacity: 0.92 }}>
                    {confirmFriosCita.mensaje ||
                      'Solo se puede sacar de Fríos creando una cita. Al confirmarla el lead se añade al cliente, pasa a Activos y se reasigna a tu agente.'}
                  </p>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <button
                      type="button"
                      onClick={async () => {
                        const clienteId = confirmFriosCita.cliente_existente?.id;
                        setTaskCodeInicial('TC');
                        setConfirmFriosCita(null);
                        await abrirModalCitaAntesDeAlta({
                          forzarClienteId: clienteId,
                          taskCodeInicial: 'TC',
                        });
                      }}
                      style={{ flex: 1, padding: '8px 12px', borderRadius: 6, fontSize: 11, fontWeight: 600, background: ml.blue, color: '#fff', border: 'none', cursor: 'pointer', fontFamily: 'inherit' }}
                    >
                      Crear cita y sacar de Fríos
                    </button>
                    <button
                      type="button"
                      onClick={() => setConfirmFriosCita(null)}
                      style={{ padding: '8px 12px', borderRadius: 6, fontSize: 11, fontWeight: 500, background: 'transparent', color: c.textMuted, border: `1px solid ${c.border}`, cursor: 'pointer', fontFamily: 'inherit' }}
                    >
                      Cancelar
                    </button>
                  </div>
                </div>
              </div>
            )}

            {confirmLeadSuelto && (
              <div ref={el => { if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' }); }} style={{ padding: '12px 22px 0' }}>
                <div style={{ padding: 14, borderRadius: 10, fontSize: 12, lineHeight: 1.6, background: isDark ? '#1e2a3a' : '#eff6ff', border: `1px solid ${isDark ? '#1d4ed860' : '#93c5fd'}`, color: isDark ? '#bfdbfe' : '#1e3a8a' }}>
                  <p style={{ fontWeight: 700, marginBottom: 8, fontSize: 13 }}>ℹ️ {confirmLeadSuelto.mensaje || 'Ya hay un lead sin cliente con el mismo teléfono o email.'}</p>
                  {confirmLeadSuelto.lead_existente && (
                    <div style={{ marginBottom: 12, fontSize: 12 }}>
                      <div style={{ fontWeight: 600 }}>{confirmLeadSuelto.lead_existente.nombre || 'Lead'}</div>
                      <div>{confirmLeadSuelto.lead_existente.email || '—'}</div>
                      <div>{confirmLeadSuelto.lead_existente.telefono || '—'}</div>
                      {confirmLeadSuelto.lead_existente.etapa && <div style={{ marginTop: 4, opacity: 0.85 }}>Etapa: {confirmLeadSuelto.lead_existente.etapa}</div>}
                    </div>
                  )}
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    <button type="button" onClick={() => { const id = confirmLeadSuelto.lead_existente?.id; if (id) { setConfirmLeadSuelto(null); navigate(`/pipeline?openLead=${id}`); onClose?.(); } }} style={{ flex: 1, minWidth: 140, padding: '8px 12px', borderRadius: 6, fontSize: 11, fontWeight: 600, background: ml.blue, color: '#fff', border: 'none', cursor: 'pointer', fontFamily: 'inherit' }}>
                      Abrir lead existente
                    </button>
                    <button type="button" onClick={() => handleGuardar(undefined, { ignorarLeadSuelto: true, agenda: pendingAltaOptsRef.current?.agenda })} style={{ flex: 1, minWidth: 140, padding: '8px 12px', borderRadius: 6, fontSize: 11, fontWeight: 600, background: 'transparent', color: c.textPrimary, border: `1px solid ${c.border}`, cursor: 'pointer', fontFamily: 'inherit' }}>
                      Crear otro lead igualmente
                    </button>
                    <button type="button" onClick={() => setConfirmLeadSuelto(null)} style={{ padding: '8px 12px', borderRadius: 6, fontSize: 11, fontWeight: 500, color: c.textMuted, border: 'none', background: 'transparent', cursor: 'pointer', fontFamily: 'inherit' }}>
                      Cancelar
                    </button>
                  </div>
                </div>
              </div>
            )}

            {confirmPhoneReview && (
              <div ref={el => { if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' }); }} style={{ padding: '12px 22px 0' }}>
                <div style={{ padding: 14, borderRadius: 10, fontSize: 12, lineHeight: 1.6, background: isDark ? '#2a2010' : '#fffbeb', border: `1px solid ${isDark ? '#5c4a1d' : '#fde68a'}`, color: isDark ? '#fcd34d' : '#92400e' }}>
                  <p style={{ fontWeight: 700, marginBottom: 8, fontSize: 13 }}>
                    ⚠️ Revisión telefónica probable
                  </p>
                  <p style={{ marginTop: 0, marginBottom: 10, opacity: 0.92 }}>
                    {confirmPhoneReview.mensaje || 'El teléfono se parece a otros registros y necesita revisión antes de crear el lead.'}
                  </p>
                  <div style={{ display: 'grid', gap: 8, marginBottom: 12 }}>
                    {(confirmPhoneReview.duplicate_candidates || []).map((item) => (
                      <div key={`${item.entity_type}-${item.id}`} style={{ borderRadius: 8, border: `1px solid ${c.border}`, padding: 10, background: c.bgCard, color: c.textPrimary }}>
                        <div style={{ fontWeight: 700 }}>{item.display_name || 'Registro existente'}</div>
                        <div style={{ fontSize: 11, color: c.textMuted, marginTop: 4 }}>
                          {item.telefono || 'Sin teléfono'}
                          {item.email ? ` · ${item.email}` : ''}
                          {item.agente_nombre ? ` · agente ${item.agente_nombre}` : ''}
                        </div>
                        <div style={{ fontSize: 11, color: c.textMuted, marginTop: 4 }}>
                          {item.reason || 'Coincidencia probable'}
                        </div>
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
                          <button
                            type="button"
                            onClick={() => {
                              setConfirmPhoneReview(null);
                              if (item.entity_type === 'cliente') navigate(`/clientes/${item.id}`);
                              else if (item.entity_type === 'contacto') navigate(`/contactos/${item.id}`);
                              else navigate(`/pipeline?openLead=${item.id}`);
                              onClose?.();
                            }}
                            style={{ padding: '8px 12px', borderRadius: 6, fontSize: 11, fontWeight: 600, background: ml.blue, color: '#fff', border: 'none', cursor: 'pointer', fontFamily: 'inherit' }}
                          >
                            Abrir ficha
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                  {confirmPhoneReview.must_fix_phone && (
                    <div style={{ fontSize: 11, marginBottom: 12, color: isDark ? '#fde68a' : '#92400e' }}>
                      Debes corregir el teléfono antes de crear el lead.
                    </div>
                  )}
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    <button type="button" onClick={() => setConfirmPhoneReview(null)} style={{ padding: '8px 12px', borderRadius: 6, fontSize: 11, fontWeight: 500, color: c.textMuted, border: `1px solid ${c.border}`, background: 'transparent', cursor: 'pointer', fontFamily: 'inherit' }}>
                      Cerrar
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Error */}
            {error && !confirmLeadSuelto && !confirmPhoneReview && (
              <div style={{ padding: '12px 22px 0' }}>
                <div style={{ padding: '10px 14px', borderRadius: 8, fontSize: 13, background: isDark ? '#2a1215' : '#fef2f2', border: `1px solid ${isDark ? '#5c1d1d' : '#fecaca'}`, color: '#ef4444' }}>
                  {error}
                </div>
              </div>
            )}

            <div style={{ height: 8 }} />
          </div>
        </div>

        {/* ══════ PANEL DERECHA: historial (edición) o asignar tarea (alta manual) ══════ */}
        {modoEdicion ? (
          <PanelHistorial
            lead={lead}
            isDark={isDark}
            c={c}
            soloLectura={leadArchivado}
            onLeadRefresh={onLeadUpdated}
            panelOpen={isOpen}
            taskOptions={taskOptions}
            onPedirModalCita={async ({ lead: leadCtx, pendingTaskCode }) => {
              let propiedades = [];
              const ref = String(leadCtx?.propiedad_ref || form.propiedad_ref || '').trim();
              if (ref) {
                try {
                  const prop = await fetchPropiedadPorRef(api, ref);
                  if (prop) propiedades = [prop];
                } catch { /* ignore */ }
              }
              const agenteSel = agentes.find((a) => String(a.id) === String(leadCtx?.agente_id || form.agente_id || ''));
              setModalCitaAlta({
                draft: true,
                editAssign: true,
                leadId: leadCtx?.id,
                pendingTaskCode: pendingTaskCode || 'TC',
                propiedades,
                agentName: agenteSel?.nombre || '',
                contactNombre: `${leadCtx?.nombre || ''} ${leadCtx?.apellido || ''}`.trim(),
                contactTelefono: leadCtx?.telefono || '',
                contactEmail: leadCtx?.email || '',
              });
            }}
          />
        ) : (
          <div style={{
            width: 320, flexShrink: 0, borderLeft: `1px solid ${c.border}`,
            display: 'flex', flexDirection: 'column', background: isDark ? c.bgCard : '#f8fafc',
            minHeight: 0,
          }}>
            <div style={{ padding: '14px 16px' }}>
              <div style={{ fontSize: 10, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.05em', color: isDark ? c.textPrimary : '#1d4ed8', marginBottom: 8 }}>
                Tarea vigente
              </div>
              <AsignarTareaFlujoBlock
                options={taskOptions}
                value={taskCodeInicial}
                highlightError={errorTareaInicial}
                c={c}
                isDark={isDark}
                onSelect={(code) => {
                  setTaskCodeInicial(code);
                  setErrorTareaInicial(false);
                  if (
                    error === MSG_DEBE_ASIGNAR_TAREA ||
                    error === MSG_CITA_CANCELADA_ELIGE_TAREA
                  ) {
                    setError('');
                  }
                }}
              />
            </div>
          </div>
        )}

        </div>{/* fin cuerpo dos columnas */}

        {/* Footer a ancho completo */}
        <div style={{ padding: '14px 22px 18px', display: 'flex', gap: 10, justifyContent: 'flex-end', flexShrink: 0, borderTop: `1px solid ${isDark ? c.border : '#a8a8a0'}`, background: isDark ? 'transparent' : '#e0e0d8' }}>
          <button type="button" onClick={() => { onClose?.(); resetForm(); }} style={{ padding: '10px 22px', borderRadius: 10, border: `1.5px solid ${isDark ? c.border : '#a8a8a0'}`, background: isDark ? c.bgInput : '#ffffff', color: c.textPrimary, cursor: 'pointer', fontFamily: 'inherit', fontSize: 13, fontWeight: 600 }}>
            Cancelar
          </button>
          <button type="button" onClick={() => handleGuardar()} disabled={guardando || leadArchivado || confirmCliente || activosBloqueoVigente || confirmFriosCita || confirmLeadSuelto || confirmPhoneReview} style={{ padding: '10px 28px', borderRadius: 10, border: 'none', background: guardando || leadArchivado || confirmCliente || activosBloqueoVigente || confirmFriosCita || confirmLeadSuelto || confirmPhoneReview ? c.bgInput : `linear-gradient(135deg, ${ml.blueFrom}, ${ml.blueTo})`, color: guardando || leadArchivado || confirmCliente || activosBloqueoVigente || confirmFriosCita || confirmLeadSuelto || confirmPhoneReview ? c.textMuted : '#fff', cursor: guardando || leadArchivado || confirmCliente || activosBloqueoVigente || confirmFriosCita || confirmLeadSuelto || confirmPhoneReview ? 'not-allowed' : 'pointer', fontFamily: 'inherit', fontSize: 13, fontWeight: 700, boxShadow: guardando || leadArchivado || confirmCliente || activosBloqueoVigente || confirmFriosCita || confirmLeadSuelto || confirmPhoneReview ? 'none' : `0 4px 14px ${ml.blueShadow}`, display: 'flex', alignItems: 'center', gap: 8, opacity: guardando || leadArchivado || confirmCliente || activosBloqueoVigente || confirmFriosCita || confirmLeadSuelto || confirmPhoneReview ? 0.5 : 1 }}>
            {modoEdicion ? (
              <>
                <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                  <path d="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v11a2 2 0 01-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/>
                </svg>
                {guardando ? 'Guardando...' : 'Guardar cambios'}
              </>
            ) : (
              <>
                <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                  <path d="M16 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="8.5" cy="7" r="4"/><line x1="20" y1="8" x2="20" y2="14"/><line x1="23" y1="11" x2="17" y2="11"/>
                </svg>
                {guardando ? 'Creando...' : 'Crear lead'}
              </>
            )}
          </button>
        </div>

        {modalMoverDestino && lead?.id && (
          <ModalMoverLeadTerminal
            destino={modalMoverDestino}
            lead={lead}
            isDark={isDark}
            c={c}
            onClose={() => setModalMoverDestino(null)}
            onExito={() => {
              setModalMoverDestino(null);
              resetForm();
              // onClose ya recarga embudo + limpia leadEditando (evitar doble
              // onCreated/onClose que dejaba el modal abierto tras un move OK).
              onClose?.();
            }}
          />
        )}

        {vistaPreviaPropId && (
          <FichaResumenPropiedad
            propiedadId={vistaPreviaPropId}
            onClose={() => setVistaPreviaPropId(null)}
          />
        )}

      </div>

      {/* Fuera del card (overflow:hidden) y por encima del alta: mismo ModalCita que M1. */}
      {modalCitaAlta?.draft && (
        <ModalCita
          isOpen
          zIndex={1200}
          onClose={cerrarModalCitaAltaSinVisita}
          onConfirm={handleCitaAltaConfirm}
          contact={{
            nombre: modalCitaAlta.contactNombre
              || `${form.nombre || ''} ${form.apellido || ''}`.trim(),
            telefono: modalCitaAlta.contactTelefono || form.telefono || '',
            email: modalCitaAlta.contactEmail || form.email || '',
          }}
          properties={modalCitaAlta.propiedades || []}
          agentName={modalCitaAlta.agentName || ''}
        />
      )}
    </div>
  );
}
